import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const SECTIONS = [
  { id: 'domain-sbom',       label: '📦 SBOM Lifecycle',          isDomain: true },
  { id: 'sbom',              icon: '📋' },
  { id: 'upload',            icon: '⬆️' },
  { id: 'quality',           icon: '🏆' },
  { id: 'diff',              icon: '🔀' },
  { id: 'graph',             icon: '🕸️' },
  { id: 'impact-graph',      icon: '🌐' },

  { id: 'domain-security',   label: '🛡️ Security Scanning',       isDomain: true },
  { id: 'vulnerabilities',   icon: '🛡️' },
  { id: 'csaf',              icon: '📡' },
  { id: 'malware',           icon: '🦠' },
  { id: 'maintainer',        icon: '👤' },
  { id: 'abandoned',         icon: '👻' },
  { id: 'depguard-reach',    icon: '🔍' },
  { id: 'slsa',              icon: '🔐' },

  { id: 'domain-compliance', label: '🏛️ Compliance & Governance', isDomain: true },
  { id: 'compliance-fw',     icon: '🏛️' },
  { id: 'matrix',            icon: '📊' },

  { id: 'domain-risk',       label: '📊 Risk Intelligence',       isDomain: true },
  { id: 'health',            icon: '🏥' },
  { id: 'licenses',          icon: '⚖️' },
  { id: 'license-risk',      icon: '🏷️' },
  { id: 'trends',            icon: '📈' },
  { id: 'supplier-trust',    icon: '🤝' },

  { id: 'domain-policy',     label: '📋 Policy & Remediation',    isDomain: true },
  { id: 'policy',            icon: '📋' },
  { id: 'vex',               icon: '🔏' },
  { id: 'remediation',       icon: '📋' },
  { id: 'evidence-pack',     icon: '📦' },
  { id: 'cicd',              icon: '⚡' },
  { id: 'webhooks',          icon: '🔔' },

  { id: 'domain-ai',         label: '🤖 AI & Automation',         isDomain: true },
  { id: 'ai',                icon: '🤖' },
  { id: 'ai-agent',          icon: '🤖' },

  { id: 'domain-vigil',      label: '🔎 Vigil SAST',              isDomain: true },
  { id: 'vigil-sast',        icon: '🔎' },
  { id: 'vigil-studio',      icon: '🎨' },
  { id: 'vigil-academy',     icon: '🎓' },

  { id: 'domain-platform',   label: '⚙️ Platform',                isDomain: true },
  { id: 'auth',              icon: '🔐' },
  { id: 'glossary',          icon: '📖' },
];

// Glossary terms with i18n keys for definitions
const GLOSSARY_KEYS = [
  ['SBOM',        'sbom'],
  ['SPDX',        'spdx'],
  ['CycloneDX',   'cyclonedx'],
  ['CVE',         'cve'],
  ['CVSS',        'cvss'],
  ['GHSA',        'ghsa'],
  ['OSV',         'osv'],
  ['PURL',        'purl'],
  ['CPE',         'cpe'],
  ['Identifier Quality', 'identifierQuality'],
  ['NTIA',        'ntia'],
  ['OpenSSF',     'openssf'],
  ['Scorecard',   'scorecard'],
  ['Copyleft',    'copyleft'],
  ['NOASSERTION', 'noassertion'],
  ['SCA',         'sca'],
  ['VEX',         'vex'],
  ['CRA',         'cra'],
  ['EO 14028',    'eo14028'],
  ['Risk Score',  'riskScore'],
  ['Compliance Matrix', 'complianceMatrix'],
  ['NIST SSDF',   'nistSsdf'],
  ['License Risk',         'licenseRisk'],
  ['Strong Copyleft',      'strongCopyleft'],
  ['Weak Copyleft',        'weakCopyleft'],
  ['Unknown License',      'unknownLicense'],
  ['SPDX family match',    'spdxFamilyMatch'],
  ['License Distribution', 'licenseDistribution'],
  ['VEX (detailed)',       'vex'],
  ['VEX Status',           'vexStatus'],
  ['VEX Justification',    'vexJustification'],
  ['FixedIn',              'fixedIn'],
  ['Dependency Graph',     'dependencyGraph'],
  ['Remediation',          'remediation'],
  ['Remediation Status',   'remediationStatus'],
  ['Accepted Risk',        'acceptedRisk'],
  ['Overdue',              'overdue'],
  ['NTIA minimum elements','ntiaMinElements'],
  ['OSV (detailed)',       'osv'],
  ['Abandoned package',    'abandonedPackage'],
  ['Archived repository',  'archivedRepo'],
  ['SBOM Diff',            'sbomDiff'],
  ['NIST SP 800-53',       'nist80053'],
  ['ISO 27001',            'iso27001'],
  ['EU CRA',               'euCra'],
  ['JWT',                  'jwt'],
  ['RBAC',                 'rbac'],
  ['Bearer token',         'bearerToken'],
  ['Audit log',            'auditLog'],
  ['Evidence Pack',        'evidencePack'],
  ['Admin',                'admin'],
  ['Analyst',              'analyst'],
  ['Autonomous AI Agent',  'autonomousAgent'],
  ['SLSA',                 'slsa'],
  ['SLSA Provenance',      'slsaProvenance'],
  ['Build Attestation',    'buildAttestation'],
  ['Typosquatting',        'typosquatting'],
  ['Reachability Analysis', 'reachabilityAnalysis'],
  ['Supplier Trust',       'supplierTrust'],
];

function WhyItMatters({ children }) {
  const { t } = useTranslation();
  return (
    <div className="why-it-matters" style={{
      background: 'var(--accent-light)',
      borderLeft: '3px solid var(--accent)',
      borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
      padding: '0.875rem 1.125rem',
      marginTop: '1rem',
      marginBottom: '1rem',
    }}>
      <div style={{ fontWeight: 600, fontSize: '0.8125rem', color: 'var(--accent-text)', marginBottom: '0.375rem' }}>
        💡 {t('docs.whyItMatters')}
      </div>
      <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.6 }}>
        {children}
      </p>
    </div>
  );
}

function Pills({ terms }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginTop: '0.75rem' }}>
      {terms.map(t => (
        <span key={t} style={{
          fontSize: '0.6875rem', fontWeight: 600,
          padding: '0.2rem 0.6rem',
          borderRadius: '100px',
          background: 'var(--bs-tertiary-bg)',
          color: 'var(--bs-secondary-color)',
          border: '1px solid var(--bs-border-color)',
        }}>{t}</span>
      ))}
    </div>
  );
}

function SectionHeading({ icon, title }) {
  return (
    <h2 style={{ fontSize: '1.375rem', fontWeight: 700, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <span>{icon}</span> {title}
    </h2>
  );
}

export default function Docs() {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState('sbom');

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    const handleScroll = () => {
      const offsets = SECTIONS
        .filter(s => !s.isDomain)
        .map(s => {
          const el = document.getElementById(s.id);
          if (!el) return { id: s.id, top: Infinity };
          return { id: s.id, top: el.getBoundingClientRect().top };
        });
      const visible = offsets.filter(o => o.top <= 120);
      if (visible.length > 0) {
        setActiveId(visible[visible.length - 1].id);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="App-body">
        <div className="ce-page-header" style={{ paddingLeft: 0, paddingRight: 0, marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              <h1 className="ce-page-title">📖 {t('docs.title')}</h1>
              <p className="ce-page-subtitle">{t('docs.subtitle')}</p>
          </div>
          <button
            className="btn btn-primary no-print"
            onClick={handlePrint}
            title="Save this documentation as PDF using your browser's Print → Save as PDF"
            style={{ whiteSpace: 'nowrap', alignSelf: 'center' }}
            >
              🖨️ {t('docs.downloadPdf')}
            </button>
          </div>
        </div>

        <div
          style={{
            background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(59,130,246,0.08))',
            border: '1px solid var(--bs-border-color)',
            borderRadius: 12,
            padding: '1rem 1.25rem',
            marginBottom: '1.5rem',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: '0.35rem' }}>{t('docs.intelligenceCalloutTitle')}</div>
          <div style={{ lineHeight: 1.7, marginBottom: '0.75rem' }}>{t('docs.intelligenceCalloutBody')}</div>
          <div className="d-flex gap-2 flex-wrap">
            <Link className="btn btn-outline-primary btn-sm" to="/docs/intelligence">
              {t('docs.intelligenceCalloutAction')}
            </Link>
            <Link className="btn btn-outline-secondary btn-sm" to="/docs/intelligence/rule-roadmap">
              {t('docs.intelligenceRoadmapAction')}
            </Link>
          </div>
        </div>

        <div
          style={{
            background: 'linear-gradient(135deg, rgba(16,185,129,0.10), rgba(59,130,246,0.08))',
            border: '1px solid var(--bs-border-color)',
            borderRadius: 12,
            padding: '1rem 1.25rem',
            marginBottom: '1.5rem',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>{t('docs.content.recentAdditions.title', 'Recent platform additions')}</div>
          <div style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
            {t('docs.content.recentAdditions.body', 'DepGuard now exposes two important customer-facing workflows directly in the product...')}
          </div>
          <ul style={{ marginTop: '0.75rem', marginBottom: 0, paddingLeft: '1.25rem', lineHeight: 1.8 }}>
            <li><strong>Supplier Trust</strong> combines malware, typosquatting, maintainer health, abandoned-package risk, reachability context, and SLSA provenance into one portfolio signal.</li>
            <li><strong>Evidence Pack</strong> downloads one ZIP with the selected SBOM, CRA JSON/HTML, remediation records, reachability, SLSA, supplier trust, audit log, and a manifest of file digests.</li>
          </ul>
        </div>

      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '2.5rem', alignItems: 'start' }}>

        {/* Sticky TOC */}
        <div className="docs-layout-toc" style={{ position: 'sticky', top: '1.5rem' }}>
          <div className="section-title" style={{ marginBottom: '0.625rem' }}>{t('docs.contents')}</div>
          {SECTIONS.map(s => s.isDomain ? (
            <div
              key={s.id}
              style={{
                fontSize: '0.6875rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--bs-secondary-color)',
                padding: '0.625rem 0.625rem 0.2rem',
                marginTop: '0.25rem',
              }}
            >
              {s.label}
            </div>
          ) : (
            <a
              href={`#${s.id}`}
              key={s.id}
              style={{
                display: 'block',
                padding: '0.25rem 0.625rem 0.25rem 1.125rem',
                borderRadius: 'var(--radius-xs)',
                fontSize: '0.8125rem',
                fontWeight: activeId === s.id ? 600 : 400,
                color: activeId === s.id ? 'var(--accent-text)' : 'var(--bs-secondary-color)',
                background: activeId === s.id ? 'var(--accent-light)' : 'transparent',
                textDecoration: 'none',
                marginBottom: '1px',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              {s.icon} {t(`docs.sections.${s.id}`)}
            </a>
          ))}
        </div>

        {/* Content */}
        <div className="docs-layout-content">

          {/* Print-only cover header (hidden on screen) */}
          <div className="docs-print-header" style={{ display: 'none' }}>
            <div style={{ fontSize: '24pt', marginBottom: '0.5rem' }}>🛡️ DepGuard</div>
            <div style={{ fontSize: '16pt', fontWeight: 700, color: '#1e3a5f' }}>Documentation</div>
            <div style={{ fontSize: '10pt', color: '#555', marginTop: '0.5rem' }}>
              SBOM &amp; Dependency Security Platform — Generated {new Date().toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>

          {/* ═══ Domain: 📦 SBOM Lifecycle ═══ */}
          <div id="domain-sbom" style={{ marginBottom: '2rem' }}>
            <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'var(--bs-secondary-color)',
              marginBottom: '0.25rem',
            }}>
              📦 SBOM Lifecycle
            </div>
          </div>

          <section id="sbom" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="📋" title={t('docs.sections.sbom')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.sbom.intro', 'A Software Bill of Materials (SBOM) is a formal, machine-readable inventory of all software components, libraries, and dependencies present in an application.')}
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.sbom.formats', 'An SBOM typically contains: component names and versions, licenses, supplier information, relationships between components...')}
            </p>
            <WhyItMatters>
              {t('docs.content.sbom.whyItMatters', 'High-profile supply chain attacks exposed the catastrophic consequences of not knowing what is inside your software.')}
            </WhyItMatters>
            <Pills terms={['SPDX', 'CycloneDX', 'PURL', 'CPE', 'Component', 'Supplier', 'SBOM Baseline', 'ISO 5962']} />
          </section>

          <section id="upload" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="⬆️" title={t('docs.sections.upload')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              DepGuard accepts SBOM uploads in <strong>SPDX-JSON</strong> and <strong>CycloneDX-JSON</strong> formats
              (up to 50 MB). Uploaded SBOMs are stored with a commit hash reference and immediately queued for
              vulnerability scanning, health analysis, quality scoring, and policy evaluation.
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              If you don't yet have an SBOM, the Upload page provides ready-to-use commands for the three most popular
              SBOM generators:
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li>
                <strong>Syft</strong> (by Anchore — recommended): Supports 40+ ecosystems including npm, Maven, pip,
                Go, Rust, Ruby, and containers. Generates both SPDX-JSON and CycloneDX-JSON.
                <br /><code style={{ fontSize: '0.8125rem' }}>syft . -o spdx-json=sbom.json</code>
              </li>
              <li>
                <strong>cdxgen</strong> (by OWASP): Specialist for JavaScript, Java, and Python. Generates
                CycloneDX-JSON with rich dependency graph data.
                <br /><code style={{ fontSize: '0.8125rem' }}>cdxgen -o sbom.json</code>
              </li>
              <li>
                <strong>Trivy</strong> (by Aqua Security): Container and filesystem focused; combines SBOM generation
                with inline vulnerability scanning.
                <br /><code style={{ fontSize: '0.8125rem' }}>trivy fs --format spdx-json --output sbom.json .</code>
              </li>
            </ul>
            <WhyItMatters>
              {t('docs.content.upload.whyItMatters', 'DepGuard is deliberately ecosystem-agnostic.')}
            </WhyItMatters>
            <Pills terms={['SPDX-JSON', 'CycloneDX-JSON', 'Syft', 'cdxgen', 'Trivy', 'CI/CD', 'Commit hash', 'Ecosystem-agnostic']} />
          </section>

          <section id="quality" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🏆" title={t('docs.sections.quality')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.quality.intro', 'Not all SBOMs are created equal. A poorly generated SBOM provides false confidence.')}
            </p>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
              <thead>
                <tr>
                  <th>Dimension</th>
                  <th style={{ width: '80px' }}>Weight</th>
                  <th>What it checks</th>
                </tr>
              </thead>
              <tbody>
                <tr><td><strong>Completeness</strong></td><td>30%</td><td>Packages have name + version + license + identifier</td></tr>
                <tr><td><strong>Identifiers</strong></td><td>25%</td><td>Packages carry a PURL or CPE for unambiguous matching</td></tr>
                <tr><td><strong>License Quality</strong></td><td>20%</td><td>Concrete SPDX license IDs vs. NOASSERTION / NONE</td></tr>
                <tr><td><strong>Metadata</strong></td><td>15%</td><td>SBOM has creator, timestamp, namespace, and tool info</td></tr>
                <tr><td><strong>Version Completeness</strong></td><td>10%</td><td>Packages carry explicit version strings (not "latest")</td></tr>
              </tbody>
            </table>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.quality.ntia', 'DepGuard also checks NTIA minimum element compliance — the seven fields the US NTIA requires per component.')}
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.quality.identifiers', 'The Identifiers dimension is not just a presence check anymore. DepGuard validates PURLs and CPEs.')}
            </p>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
              <thead>
                <tr><th>NTIA Element</th><th>What is checked</th></tr>
              </thead>
              <tbody>
                <tr><td><strong>Author of SBOM Data</strong></td><td>At least one creator entry in <code>creationInfo.creators</code></td></tr>
                <tr><td><strong>Timestamp</strong></td><td><code>creationInfo.created</code> is present and non-empty</td></tr>
                <tr><td><strong>Component Name</strong></td><td>Every package has a non-empty <code>name</code> field</td></tr>
                <tr><td><strong>Component Version</strong></td><td>Every package has a non-empty <code>versionInfo</code> field</td></tr>
                <tr><td><strong>Unique Identifiers</strong></td><td>Every package has at least one PURL or CPE in <code>externalRefs</code></td></tr>
                <tr><td><strong>Supplier Name</strong></td><td>Every package has a <code>supplier</code> field that is not NOASSERTION</td></tr>
                <tr><td><strong>Dependency Relationships</strong></td><td>At least one <code>DEPENDS_ON</code> relationship is present in the SBOM</td></tr>
              </tbody>
            </table>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7, fontSize: '0.875rem' }}>
              {t('docs.content.quality.failing', 'Failing elements show the number of packages affected. This makes it actionable.')}
            </p>
            <WhyItMatters>
              {t('docs.content.quality.whyItMatters', 'Missing PURLs mean vulnerability scanners cannot reliably match packages to advisory databases.')}
            </WhyItMatters>
            <Pills terms={['NTIA minimum elements', 'Identifier Quality', 'NOASSERTION', 'Grade A/B/C/D/F', 'PURL', 'CPE', 'Document namespace', 'CreationInfo']} />
          </section>

          <section id="diff" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🔀" title={t('docs.sections.diff')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.diff.intro', 'The SBOM Diff view allows you to compare any two SBOM snapshots for the same repository side-by-side.')}
            </p>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              <thead><tr><th>Status</th><th>Color</th><th>Meaning</th></tr></thead>
              <tbody>
                <tr><td>➕ Added</td><td><span className="badge bg-success">green</span></td><td>Package present in the new SBOM but not in the old one</td></tr>
                <tr><td>➖ Removed</td><td><span className="badge bg-danger">red</span></td><td>Package present in the old SBOM but dropped in the new one</td></tr>
                <tr><td>🔄 Updated</td><td><span className="badge bg-warning text-dark">yellow</span></td><td>Same package name, different version between the two SBOMs</td></tr>
              </tbody>
            </table>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Select a "From" and "To" SBOM using the dropdowns, then click <strong>Compare</strong>. Use the
              filter buttons to show only added, removed, or updated entries.
            </p>
            <WhyItMatters>
              {t('docs.content.diff.whyItMatters', 'SBOMs capture a moment in time. Comparing snapshots across releases lets you audit exactly what changed.')}
            </WhyItMatters>
            <Pills terms={['SBOM snapshot', 'Diff', 'Added', 'Removed', 'Updated', 'Release gating', 'Supply chain audit']} />
          </section>

          <section id="graph" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🕸️" title={t('docs.sections.graph')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.graph.intro', 'The Dependency Graph visualizes the full tree of a project\'s dependencies — including transitive (indirect) dependencies.')}
            </p>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Node colors
            </h4>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              <thead><tr><th>Color</th><th>Meaning</th></tr></thead>
              <tbody>
                <tr><td><span style={{ color: '#dc2626', fontWeight: 700 }}>🔴 Red</span></td><td>Package has one or more Critical or High CVEs</td></tr>
                <tr><td><span style={{ color: '#d97706', fontWeight: 700 }}>🟠 Orange</span></td><td>Package has Medium severity CVEs</td></tr>
                <tr><td><span style={{ color: '#2563eb', fontWeight: 700 }}>🔵 Blue</span></td><td>Package has Low severity CVEs only</td></tr>
                <tr><td><span style={{ color: '#6b7280', fontWeight: 700 }}>⚪ Grey</span></td><td>No known vulnerabilities</td></tr>
              </tbody>
            </table>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Requirements for graph edges
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Dependency edges (arrows) are only available when the SBOM was generated in <strong>CycloneDX format</strong>
              with a tool that captures transitive dependencies (e.g. <code>cyclonedx-gomod</code>, <code>cyclonedx-maven-plugin</code>,
              or <code>@cyclonedx/cyclonedx-npm</code>). SPDX-format SBOMs and SBOMs generated by <code>syft</code> without
              explicit dependency flags typically do not contain relationship data — the graph will show nodes only.
            </p>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              <thead><tr><th>Ecosystem</th><th>Recommended command</th></tr></thead>
              <tbody>
                <tr><td>Go</td><td><code>cyclonedx-gomod app -json -output sbom.json -main .</code></td></tr>
                <tr><td>Maven (Java)</td><td><code>mvn org.cyclonedx:cyclonedx-maven-plugin:makeAggregateBom</code></td></tr>
                <tr><td>npm / Node.js</td><td><code>npx @cyclonedx/cyclonedx-npm --output-format JSON --output-file sbom.json</code></td></tr>
                <tr><td>Python (pip)</td><td><code>cyclonedx-py environment -o cyclonedx-json &gt; sbom.json</code></td></tr>
              </tbody>
            </table>
            <WhyItMatters>
              {t('docs.content.graph.whyItMatters', 'Transitive dependencies are where most real-world supply chain risks hide.')}
            </WhyItMatters>
            <Pills terms={['CycloneDX', 'dependsOn', 'transitive dependency', 'cyclonedx-gomod', 'cyclonedx-maven-plugin', 'SPDX', 'supply chain']} />
          </section>

          <section id="impact-graph" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🌐" title={t('docs.sections.impact-graph')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The Cross-Project Dependency Impact Graph answers one of the most critical questions for enterprise security teams:
              <em> "Which of our projects are affected by this vulnerable package?"</em>
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              It is an interactive force-graph at <strong>/impact</strong> that visualizes package nodes connected to all repositories
              using them. When a critical CVE drops, you can look up the affected package and immediately see — in a single view —
              the blast radius across your entire portfolio.
            </p>
            <WhyItMatters>
              Without this view, a security team must manually check each repository's SBOM to find which projects use a vulnerable
              package. For an organization with 500+ repositories, that is a multi-hour manual process. The Impact Graph reduces it to seconds.
            </WhyItMatters>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Features
            </h4>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li><strong>Graph view</strong> — D3 force-graph with CRITICAL nodes pulsing red, package → project edges, click a project node to open a side panel</li>
              <li><strong>Table view</strong> — switch to a sortable table of all affected repositories (9 sortable columns: repo name, risk score, CVE severity, policy status, SBOM grade, etc.)</li>
              <li><strong>Filters</strong> — filter by CVE severity (CRITICAL/HIGH/MEDIUM/LOW), policy status (pass/fail), SBOM grade (A–F), free-text search</li>
              <li><strong>Side panel</strong> — clicking a project node shows: CVE list (with click-through to repo detail), license issues, health score, SBOM grade</li>
              <li><strong>CVE click-through</strong> — CVE badges in the side panel and table navigate directly to the repository's vulnerability details</li>
              <li><strong>CSV export</strong> — download all affected repositories as CSV for management reporting or audit evidence</li>
            </ul>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              How to Use It
            </h4>
            <ol style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li>Navigate to <strong>Impact Graph</strong> in the sidebar (or press <kbd>⌘K</kbd> and type "impact").</li>
              <li>Search for a package by name (e.g. <code>log4j-core</code>) or version.</li>
              <li>The graph renders all repositories in your portfolio that include this package.</li>
              <li>Use the filter controls to narrow by severity or grade.</li>
              <li>Click a project node to open the side panel — or switch to Table view for a tabular overview.</li>
            </ol>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              API Endpoint
            </h4>
            <pre style={{ background: 'var(--bs-tertiary-bg)', padding: '0.75rem', borderRadius: '6px', fontSize: '0.8rem' }}>
{`GET /api/v1/packages/impact?name=log4j-core&version=2.14.1
→ { "package": {...}, "repos": [ {repoName, riskScore, vulns, grade, policyStatus} ] }`}
            </pre>
            <Pills terms={['Impact Graph', 'Blast radius', 'Force-graph', 'D3', 'Cross-project', 'Portfolio', 'CRITICAL', 'CVE', 'SBOM', 'CSV export']} />
          </section>

          {/* ═══ Domain: 🛡️ Security Scanning ═══ */}
          <div id="domain-security" style={{
            borderTop: '2px solid var(--bs-border-color)',
            margin: '2rem 0 2rem',
            paddingTop: '1.5rem',
          }}>
            <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'var(--bs-secondary-color)',
              marginBottom: '0.25rem',
            }}>
              🛡️ Security Scanning
            </div>
          </div>

          <section id="vulnerabilities" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🛡️" title={t('docs.sections.vulnerabilities')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.vulnerabilities.intro', 'DepGuard scans every package in your SBOM against the OSV database and the GitHub Advisory Database.')}
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.vulnerabilities.cvss', 'CVSS (Common Vulnerability Scoring System) provides a standardized 0–10 numeric score reflecting the severity of a vulnerability:')}
            </p>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
              <thead>
                <tr>
                  <th style={{ width: '130px' }}>CVSS Range</th>
                  <th>Severity</th>
                  <th>Recommended Response</th>
                </tr>
              </thead>
              <tbody>
                <tr><td><code>9.0 – 10.0</code></td><td><span style={{ color: '#ef4444', fontWeight: 700 }}>Critical</span></td><td>Immediate action required — patch or mitigate now</td></tr>
                <tr><td><code>7.0 – 8.9</code></td><td><span style={{ color: '#f97316', fontWeight: 700 }}>High</span></td><td>Patch within days</td></tr>
                <tr><td><code>4.0 – 6.9</code></td><td><span style={{ color: '#f59e0b', fontWeight: 700 }}>Medium</span></td><td>Patch within weeks</td></tr>
                <tr><td><code>0.1 – 3.9</code></td><td><span style={{ color: '#10b981', fontWeight: 700 }}>Low</span></td><td>Patch when convenient</td></tr>
              </tbody>
            </table>
            <WhyItMatters>
              {t('docs.content.vulnerabilities.whyItMatters', 'Known vulnerabilities in open-source dependencies are the #1 attack vector for modern applications.')}
            </WhyItMatters>
            <Pills terms={['CVE', 'GHSA', 'CVSS', 'OSV', 'Critical', 'High', 'Medium', 'Low', 'Fix version', 'Zero-day', 'NVD']} />
          </section>

          <section id="csaf" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="📡" title={t('docs.sections.csaf')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              CSAF (Common Security Advisory Framework) is an ISO/IEC 27098 standard for machine-readable vendor security advisories.
              While OSV.dev covers community-reported CVEs, many enterprise-critical vulnerabilities are disclosed only through vendor advisories
              — particularly from Microsoft, Red Hat, SAP, Cisco, and others. DepGuard's CSAF integration fills this gap.
            </p>
            <WhyItMatters>
              Vendor advisories often include backport information that OSV.dev cannot reflect: a vendor may ship a patched build of
              an older version that OSV marks as "affected". CSAF lets vendors communicate "your version of package X is safe even
              though the upstream version matches the CVE range." This prevents false positives and reduces unnecessary upgrade churn.
            </WhyItMatters>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Two Matching Strategies
            </h4>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
              <thead><tr><th>Strategy</th><th>How it works</th><th>Best for</th></tr></thead>
              <tbody>
                <tr>
                  <td><strong>CVE Cross-Reference</strong></td>
                  <td>Advisory aliases JSON is scanned for CVE IDs. Any package in your SBOM with a matching CVE is flagged.</td>
                  <td>Advisories that reference specific CVE IDs (most vendor advisories)</td>
                </tr>
                <tr>
                  <td><strong>Normalized Name Matching</strong></td>
                  <td>Package names are normalized (lowercase, delimiters stripped) and matched against advisory product names.</td>
                  <td>Advisories that don't reference CVE IDs but list affected product names</td>
                </tr>
              </tbody>
            </table>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              How to Get CSAF Advisories
            </h4>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li><strong>Red Hat</strong> — <code>access.redhat.com/security/data/csaf/v2/advisories/</code></li>
              <li><strong>Microsoft</strong> — MSRC at <code>msrc.microsoft.com</code> (select "Download CSAF")</li>
              <li><strong>CISA / US-CERT</strong> — <code>cisa.gov/resources-tools/resources/csaf</code></li>
              <li><strong>BSI (Germany)</strong> — <code>wid.cert-bund.de</code></li>
              <li><strong>Siemens, Cisco, SAP</strong> — vendor security portals</li>
            </ul>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Using CSAF in DepGuard
            </h4>
            <ol style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li>Open a repository → <strong>Vulnerabilities tab → CSAF sub-tab</strong>.</li>
              <li>Paste a CSAF 2.0 JSON advisory from any vendor into the import panel.</li>
              <li>DepGuard runs both matching strategies against the selected SBOM.</li>
              <li>Matches are displayed with: affected product, CVE IDs, severity, remediation note from the advisory.</li>
              <li>Import additional advisories — all imports are stored and re-matched on each SBOM upload.</li>
            </ol>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              API Endpoints
            </h4>
            <pre style={{ background: 'var(--bs-tertiary-bg)', padding: '0.75rem', borderRadius: '6px', fontSize: '0.8rem' }}>
{`GET  /api/v1/csaf                         → list all imported advisories
POST /api/v1/csaf/import                  → import a CSAF 2.0 advisory (JSON body)
GET  /api/v1/csaf/:id                     → get advisory details
DELETE /api/v1/csaf/:id                   → delete an advisory
GET  /api/v1/sbom/:repo/:sbom/csaf/matches → CSAF matches for a specific SBOM`}
            </pre>
            <Pills terms={['CSAF', 'CSAF 2.0', 'ISO 27098', 'Vendor advisory', 'Red Hat', 'Microsoft MSRC', 'CVE', 'Backport', 'EU CRA', 'OSV']} />
          </section>

          <section id="malware" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🦠" title={t('docs.sections.malware')} />

            {/* ── What is it ── */}
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.malware.intro', 'Malware & Typosquatting Detection is a proactive supply chain attack detection feature that goes beyond standard CVE scanning.')}
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The feature combines two complementary signals: <strong>OSV MAL- database entries</strong> (confirmed
              malware records in OSV.dev) and <strong>name-similarity analysis</strong> (detecting packages whose
              names are suspiciously close to popular legitimate packages — a technique called typosquatting).
            </p>

            <WhyItMatters>
              {t('docs.content.malware.whyItMatters', 'In 2024-2025, malicious package campaigns increased dramatically. These attacks are completely invisible to CVE scanners.')}
            </WhyItMatters>

            {/* ── Two detection layers ── */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Two Detection Layers
            </h3>

            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, marginTop: '1.25rem', marginBottom: '0.5rem' }}>
              Layer 1 — OSV MAL- Database
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              OSV.dev maintains a dedicated malware registry using the <code>MAL-</code> identifier prefix
              (e.g. <code>MAL-2024-1234</code>). These entries represent packages that have been confirmed as
              malicious by the OSV team or community. DepGuard already queries OSV for CVEs — the malware
              detection layer extends this query to also retrieve <code>MAL-</code> matches and surfaces them
              as a separate, high-severity finding category distinct from vulnerability findings.
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              A <code>MAL-</code> match is treated as <strong>Critical by default</strong> — there is no safe
              version of a confirmed malicious package. The recommended action is always immediate removal and
              dependency tree audit. Each finding links directly to the OSV advisory at{' '}
              <code>osv.dev/vulnerability/MAL-YYYY-NNNNN</code>.
            </p>

            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, marginTop: '1.25rem', marginBottom: '0.5rem' }}>
              Layer 2 — Multi-Algorithm Typosquatting Analysis
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              For every package in an SBOM, DepGuard runs five complementary detection algorithms against a
              curated list of popular packages per ecosystem. Each algorithm targets a different attack pattern —
              together they cover the full taxonomy of supply chain name-confusion attacks.
            </p>
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead><tr><th>Algorithm</th><th>Attack it catches</th><th>Example</th></tr></thead>
                <tbody>
                  <tr>
                    <td><strong>Jaro-Winkler similarity</strong></td>
                    <td>Simple typos — character swaps, omissions, insertions near the start</td>
                    <td><code>reqeusts</code> → <code>requests</code></td>
                  </tr>
                  <tr>
                    <td><strong>Damerau-Levenshtein distance</strong></td>
                    <td>Adjacent transpositions and multi-character edits missed by basic edit distance</td>
                    <td><code>lodahs</code> → <code>lodash</code></td>
                  </tr>
                  <tr>
                    <td><strong>Combosquatting</strong></td>
                    <td>Popular name embedded inside a longer, seemingly legitimate package name</td>
                    <td><code>react-helper-utils</code> → <code>react</code></td>
                  </tr>
                  <tr>
                    <td><strong>Prefix / suffix normalisation</strong></td>
                    <td>Common affixes (<code>node-</code>, <code>python-</code>, <code>-js</code>, <code>-dev</code>, <code>-wrapper</code>, <code>-sdk</code>) stripped before comparison</td>
                    <td><code>python-requests</code> → <code>requests</code></td>
                  </tr>
                  <tr>
                    <td><strong>Unicode / non-ASCII detection</strong></td>
                    <td>Non-ASCII or homoglyph characters (Cyrillic, Greek) substituted for Latin letters</td>
                    <td><code>pаndas</code> (Cyrillic 'а') → <code>pandas</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The final score per package is <code>max(jaro_winkler, damerau_levenshtein_similarity)</code> after
              all normalisations. Delimiter equivalence (<code>-</code> / <code>_</code> / <code>.</code>) and
              npm scope stripping (<code>@scope/pkg</code> → <code>pkg</code>) are applied before scoring.
              A finding is emitted when the score exceeds the configurable similarity threshold (default: <strong>0.85</strong>).
              The UI shows which algorithm triggered the finding via a detection method badge, alongside a
              confidence level (HIGH / MEDIUM) derived from score range.
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              A flagged package is shown with a <strong>⚠️ Potential typosquat</strong> badge alongside the
              closest legitimate package it resembles. The analyst can dismiss (false positive) or confirm
              (escalates to Critical + triggers configured webhooks).
            </p>

            {/* ── Architecture ── */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Technical Architecture
            </h3>
            <pre style={{
              background: 'var(--bs-tertiary-bg)', borderRadius: 'var(--radius-sm)',
              padding: '1rem 1.25rem', fontSize: '0.8rem', lineHeight: 1.7,
              border: '1px solid var(--bs-border-color)', overflowX: 'auto',
            }}>{`
SBOM Upload / Scan trigger
         │
         ▼
┌────────────────────────────────────────────────────────────────┐
│              Malware Detection Service                          │
│              sbom/malware_service.go                            │
│                                                                 │
│  ┌─────────────────────┐  ┌───────────────────────────────┐    │
│  │  OSV MAL- Scanner   │  │  Typosquat Analyser (v2)      │    │
│  │                     │  │                               │    │
│  │  · Batch OSV query  │  │  · Jaro-Winkler               │    │
│  │  · MAL- prefix      │  │  · Damerau-Levenshtein        │    │
│  │    filter           │  │  · Combosquatting             │    │
│  │  · Return MAL IDs   │  │  · Prefix/suffix strip        │    │
│  │  · OSV advisory link│  │  · Unicode / homoglyph        │    │
│  │                     │  │  · Delimiter normalisation    │    │
│  │                     │  │  · 800+ popular packages      │    │
│  │                     │  │    npm/PyPI/Go/Maven/NuGet/   │    │
│  │                     │  │    RubyGems                   │    │
│  └──────────┬──────────┘  └───────────────┬───────────────┘    │
│             └──────────────────┬──────────┘                     │
│                                ▼                                │
│               MalwareFindings[]                                 │
│               { pkg, type, severity,                            │
│                 malware_id | similar_to, score,                 │
│                 detection_method, confidence, ecosystem }       │
└───────────────────────────┬─────────────────────────────────────┘
                            │
                            ▼
                   SQLite: malware_findings table
                   (+ detection_method, confidence, ecosystem cols)
                            │
                            ▼
            ┌───────────────────────────────────┐
            │  GET /sbom/:repo/:sbom/malware    │  per-SBOM findings
            │  GET /portfolio/malware           │  cross-repo overview
            └───────────────────────────────────┘
`}</pre>

            {/* ── Backend Implementation ── */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Backend Implementation
            </h3>
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead><tr><th>Component</th><th>Location</th><th>Description</th></tr></thead>
                <tbody>
                  <tr><td><code>sbom/malware_service.go</code></td><td>Existing</td><td>Orchestrates OSV MAL- query + typosquat analysis in parallel; persists new fields (detection_method, confidence, ecosystem)</td></tr>
                  <tr><td><code>sbom/typosquat_analyzer.go</code></td><td>Upgraded</td><td>5 algorithms: Jaro-Winkler, Damerau-Levenshtein, combosquatting, prefix/suffix strip, Unicode/homoglyph; returns DetectionMethod + Confidence</td></tr>
                  <tr><td><code>sbom/popular_packages.go</code></td><td>Upgraded</td><td>Expanded lists: npm 400+, PyPI 300+, RubyGems 80+, NuGet 100+, Maven 80+, Go 20+ modules</td></tr>
                  <tr><td><code>db/malware_findings.go</code></td><td>Upgraded</td><td>Added columns: <code>detection_method</code>, <code>confidence</code>, <code>ecosystem</code>; new <code>GetMalwareFindingsPortfolio()</code> query</td></tr>
                  <tr><td><code>db/database.go</code></td><td>Upgraded</td><td>Schema migration adds <code>detection_method TEXT</code>, <code>confidence TEXT</code>, <code>ecosystem TEXT</code> via <code>ALTER TABLE IF NOT EXISTS</code></td></tr>
                  <tr><td><code>GET /sbom/:repo/:sbom/malware</code></td><td>Existing</td><td>Returns all malware findings for a given SBOM — confirmed MAL- matches + typosquat candidates with detection method + confidence</td></tr>
                  <tr><td><code>GET /portfolio/malware</code></td><td>New route</td><td>Cross-repo malware overview: all open findings across all repos with per-repo breakdown summary</td></tr>
                  <tr><td><code>PUT /sbom/:repo/:sbom/malware/:id/dismiss</code></td><td>Existing</td><td>Analyst dismisses a false-positive typosquat finding; logged to audit log</td></tr>
                  <tr><td><code>PUT /sbom/:repo/:sbom/malware/:id/confirm</code></td><td>Existing</td><td>Analyst confirms a typosquat; escalates to Critical finding + triggers webhook</td></tr>
                </tbody>
              </table>
            </div>

            {/* ── Popular packages strategy ── */}
            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '0.5rem' }}>
              Popular Package Lists — Strategy
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The typosquat analysis compares SBOM packages against a curated list of popular packages per ecosystem.
              These lists are embedded at build time (static Go files) and updated with each DepGuard release.
              Sources used to build the lists:
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 2, paddingLeft: '1.25rem' }}>
              <li><strong>npm</strong> — 400+ top packages by weekly downloads (npm registry API)</li>
              <li><strong>PyPI</strong> — 300+ top packages by monthly downloads (PyPI Stats API)</li>
              <li><strong>RubyGems</strong> — 80+ top gems by total downloads</li>
              <li><strong>NuGet</strong> — 100+ by NuGet gallery download count</li>
              <li><strong>Maven</strong> — 80+ by Maven Central download stats</li>
              <li><strong>Go</strong> — top modules by deps.dev dependents count</li>
            </ul>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The similarity threshold is configurable in DepGuard settings (default: <strong>0.85</strong>).
              A lower threshold increases recall (catches more attacks) at the cost of more false positives.
              Combosquatting requires the popular package name to be at least 4 characters to avoid noise.
            </p>

            {/* ── Frontend Implementation ── */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Frontend Implementation
            </h3>
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead><tr><th>Component</th><th>Description</th></tr></thead>
                <tbody>
                  <tr><td><code>MalwareView.js</code></td><td>Malware tab in RepoDetailsTabs — findings split by type with detection method badge, confidence badge, ecosystem badge, and direct OSV advisory link for MAL- findings</td></tr>
                  <tr><td>VulnerabilityView badge</td><td>OSV MAL- matches appear in the vulnerability list with a red 🦠 <strong>MALWARE</strong> badge (distinct from CVE badges)</td></tr>
                  <tr><td>SbomList indicator</td><td>SBOM list shows a 🦠 icon with count if any malware findings exist for that SBOM</td></tr>
                  <tr><td>Dashboard widget</td><td>Global dashboard shows a "Malware Findings" card alongside CVE count — red if any confirmed, amber if typosquat candidates</td></tr>
                  <tr><td>Policy engine UI</td><td>New policy conditions: "Block if malware found" and "Block if confirmed typosquat"</td></tr>
                  <tr><td>Settings page</td><td>Typosquat similarity threshold slider (0.70–0.99), ecosystem enable/disable toggles</td></tr>
                  <tr><td>CSV Export</td><td>Export all malware findings for a given SBOM as CSV for offline audit</td></tr>
                </tbody>
              </table>
            </div>

            {/* ── Example findings ── */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Example Findings
            </h3>
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead>
                  <tr><th>Package in SBOM</th><th>Type</th><th>Finding</th><th>Detection Method</th><th>Action</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>reqeusts@2.27.1</code></td>
                    <td><span style={{ color: '#f59e0b', fontWeight: 600 }}>⚠️ Typosquat</span></td>
                    <td>Similar to <code>requests</code> (score: 0.92)</td>
                    <td>Jaro-Winkler</td>
                    <td>Confirm or Dismiss</td>
                  </tr>
                  <tr>
                    <td><code>colourama@0.4.4</code></td>
                    <td><span style={{ color: '#ef4444', fontWeight: 600 }}>🦠 Malware</span></td>
                    <td><code>MAL-2024-0091</code> — credential stealer via postinstall</td>
                    <td>OSV MAL-</td>
                    <td>Remove immediately</td>
                  </tr>
                  <tr>
                    <td><code>react-helper-utils@1.0.0</code></td>
                    <td><span style={{ color: '#f59e0b', fontWeight: 600 }}>⚠️ Typosquat</span></td>
                    <td>Contains popular name <code>react</code> (combosquat)</td>
                    <td>Combosquatting</td>
                    <td>Confirm or Dismiss</td>
                  </tr>
                  <tr>
                    <td><code>python-requests@2.28.0</code></td>
                    <td><span style={{ color: '#f59e0b', fontWeight: 600 }}>⚠️ Typosquat</span></td>
                    <td>Matches <code>requests</code> after prefix strip</td>
                    <td>Prefix/Suffix</td>
                    <td>Confirm or Dismiss</td>
                  </tr>
                  <tr>
                    <td><code>pаndas@2.0.0</code> (Cyrillic 'а')</td>
                    <td><span style={{ color: '#ef4444', fontWeight: 600 }}>🦠 Malware</span></td>
                    <td>Non-ASCII character detected; matches <code>pandas</code></td>
                    <td>Unicode / Homoglyph</td>
                    <td>Remove immediately</td>
                  </tr>
                  <tr>
                    <td><code>lodahs@4.17.21</code></td>
                    <td><span style={{ color: '#ef4444', fontWeight: 600 }}>🦠 Malware</span></td>
                    <td><code>MAL-2025-0312</code> — data exfiltration to remote C2</td>
                    <td>OSV MAL-</td>
                    <td>Remove immediately</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ── Commercial Impact ── */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Commercial Impact
            </h3>
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead><tr><th>Dimension</th><th>Impact</th></tr></thead>
                <tbody>
                  <tr><td><strong>CVE scanners miss this entirely</strong></td><td>OSV, Snyk, and Dependabot only track CVEs — confirmed malicious packages have no CVE. DepGuard fills a real blind spot.</td></tr>
                  <tr><td><strong>Competitive differentiation</strong></td><td>Only Socket.dev and Snyk (partially) offer this. Dependency-Track, Mend, and Black Duck do not. A direct upsell argument.</td></tr>
                  <tr><td><strong>EU CRA alignment</strong></td><td>CRA Article 13 requires proactive vulnerability handling — malware is a direct software supply chain threat that must be addressed</td></tr>
                  <tr><td><strong>CI/CD gate integration</strong></td><td>Policy engine blocks deployments if malware is found — zero developer effort, automatic protection</td></tr>
                  <tr><td><strong>Enterprise risk reduction</strong></td><td>A single malicious package in a CI pipeline can expose cloud credentials. One catch = significant breach prevention ROI</td></tr>
                </tbody>
              </table>
            </div>

            <Pills terms={['Malware', 'Typosquatting', 'Combosquatting', 'OSV MAL-', 'Jaro-Winkler', 'Damerau-Levenshtein', 'Homoglyph', 'Unicode attack', 'Prefix/Suffix normalisation', 'Supply chain attack', 'Credential stealer', 'Postinstall script', 'npm', 'PyPI', 'RubyGems', 'EU CRA Art.13', 'CI/CD gate', 'detection_method', 'confidence']} />
          </section>

          <section id="maintainer" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="👤" title={t('docs.sections.maintainer')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Maintainer Anomaly Detection flags packages where unusual activity in the maintainer ecosystem signals a potential supply chain attack.
              The most dangerous attack vector in modern open-source supply chains is not a CVE — it is a compromised or replaced maintainer
              who publishes malicious code under a legitimate, trusted package name.
            </p>
            <WhyItMatters>
              In 2023–2024, several high-profile supply chain incidents (xz-utils, PyPI token compromise campaigns) involved attackers
              gaining maintainer access to inject malicious code. A CVE scanner cannot detect this — the packages pass all version checks.
              Anomaly detection catches the behavioral signal before a CVE is ever issued.
            </WhyItMatters>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Anomaly Signals Detected
            </h4>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
              <thead><tr><th>Signal</th><th>Description</th><th>Risk</th></tr></thead>
              <tbody>
                <tr>
                  <td><strong>Maintainer count drop</strong></td>
                  <td>Package loses a significant fraction of its maintainers in a short time</td>
                  <td><span style={{ color: '#dc2626', fontWeight: 600 }}>High</span></td>
                </tr>
                <tr>
                  <td><strong>New maintainer + rapid publish</strong></td>
                  <td>A new, previously unknown maintainer publishes a version within hours of gaining access</td>
                  <td><span style={{ color: '#dc2626', fontWeight: 600 }}>Critical</span></td>
                </tr>
                <tr>
                  <td><strong>Off-hours publish</strong></td>
                  <td>Version published at unusual hours for the timezone of the project's known maintainers</td>
                  <td><span style={{ color: '#f59e0b', fontWeight: 600 }}>Medium</span></td>
                </tr>
                <tr>
                  <td><strong>Single maintainer (no 2FA)</strong></td>
                  <td>Package has only one maintainer and the registry reports no 2FA — easy account takeover target</td>
                  <td><span style={{ color: '#f59e0b', fontWeight: 600 }}>Medium</span></td>
                </tr>
                <tr>
                  <td><strong>Download spike anomaly</strong></td>
                  <td>Abnormal download spike inconsistent with historical trend — can indicate a malicious campaign</td>
                  <td><span style={{ color: '#f59e0b', fontWeight: 600 }}>Medium</span></td>
                </tr>
              </tbody>
            </table>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Where to Find It
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Anomalies are visible in two places:
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li><strong>Repo Details → Package Health → Anomalies tab</strong> — per-package anomaly scores, severity badges, and details</li>
              <li><strong>Dashboard → Maintainer Anomalies card</strong> — portfolio-wide count of anomalous packages</li>
            </ul>
            <Pills terms={['Maintainer anomaly', 'Supply chain attack', 'xz-utils', 'Account takeover', 'Single maintainer', '2FA', 'Off-hours publish', 'Hijacked maintainer', 'EU CRA']} />
          </section>

          <section id="abandoned" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="👻" title={t('docs.sections.abandoned')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.abandoned.intro', 'DepGuard automatically detects packages that are no longer actively maintained using two independent data sources:')}
            </p>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              <thead><tr><th>Signal</th><th>Source</th><th>Threshold</th></tr></thead>
              <tbody>
                <tr>
                  <td><strong>OpenSSF Scorecard — Maintained check</strong></td>
                  <td>api.securityscorecards.dev</td>
                  <td>Score &lt; 3/10 → flagged as abandoned</td>
                </tr>
                <tr>
                  <td><strong>GitHub archived status</strong></td>
                  <td>api.github.com (no token required)</td>
                  <td>Repository <code>archived: true</code> → flagged as archived</td>
                </tr>
              </tbody>
            </table>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Each package that is flagged receives:
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li>A <code>👻 Abandoned</code> or <code>👻 Archived</code> badge in the Health tab</li>
              <li>A human-readable <strong>reason</strong> shown on hover (e.g., <em>"OpenSSF Scorecard
                'Maintained' score: 1/10 (threshold: 3)"</em> or <em>"Repository is archived on GitHub
                (read-only, no longer maintained)"</em>)</li>
              <li>An <code>abandonedReason</code> and <code>isArchived</code> field in the API response</li>
              <li>Inclusion in the risk score calculation (+15 points)</li>
            </ul>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Use the <strong>👻 Abandoned</strong> filter button in the Health tab to quickly isolate all
              unmaintained packages across your SBOM.
            </p>
            <WhyItMatters>
              {t('docs.content.abandoned.whyItMatters', 'Abandoned packages will never receive security patches for future CVEs — even if they are clean today.')}
            </WhyItMatters>
            <Pills terms={['OpenSSF Scorecard', 'Abandoned package', 'Archived', 'Maintained check', 'GitHub API', 'End of life', 'EU CRA']} />
          </section>

          <section id="depguard-reach" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🔍" title={t('docs.sections.depguard-reach')} />

            {/* USP banner */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(99,102,241,0.10))',
              border: '1px solid rgba(16,185,129,0.3)', borderRadius: '10px',
              padding: '1rem 1.5rem', marginBottom: '1.5rem',
            }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#10b981', marginBottom: '0.4rem' }}>
                🏆 Unique Selling Point — The DepGuard Differentiator
              </div>
              <p style={{ margin: 0, lineHeight: 1.7 }}>
                DepGuard is the <strong>only open-source SBOM platform</strong> with built-in, multi-language reachability analysis and
                automated VEX generation. Commercial equivalents (Snyk, Endor Labs, Socket) charge <strong>€25–98 per developer per month</strong> for
                this capability alone.
              </p>
            </div>

            {/* Alert fatigue problem */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>The Alert Fatigue Problem</h3>
            <p style={{ lineHeight: 1.7, marginBottom: '0.75rem' }}>
              A standard SBOM scanner reports every CVE in every dependency — regardless of whether the vulnerable function is ever called.
              Research shows <strong>~70% of reported CVEs are not reachable</strong> in practice.
            </p>
            <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead><tr><th>Approach</th><th>What it checks</th><th>False positive rate</th><th>Example verdict</th></tr></thead>
                <tbody>
                  <tr>
                    <td>Manifest scanning</td>
                    <td>Is package X in go.mod / package.json?</td>
                    <td style={{ color: '#ef4444' }}>Very high</td>
                    <td>"log4j found → 247 CVEs"</td>
                  </tr>
                  <tr>
                    <td>Version-range (OSV)</td>
                    <td>Is installed version in the affected range?</td>
                    <td style={{ color: '#f59e0b' }}>High (~70%)</td>
                    <td>"log4j 2.14.1 — affected"</td>
                  </tr>
                  <tr>
                    <td><strong>🔍 Reachability (DepGuard)</strong></td>
                    <td>Is the <em>exact vulnerable function</em> called from your code?</td>
                    <td style={{ color: '#10b981' }}>Low</td>
                    <td>"logger.info(userInput) → Log4Shell exploitable"</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Architecture diagram */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Architecture Overview</h3>
            <pre style={{
              background: 'var(--bs-tertiary-bg)', borderRadius: '8px', padding: '1rem 1.25rem',
              fontSize: '0.72rem', lineHeight: 1.6, overflowX: 'auto', marginBottom: '1.25rem',
              fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
            }}>{`DepGuard Portal (API)
POST /api/v1/repos/:repo/sboms/:sbom/reachability
              │  (async job queue — 202 Accepted + jobId)
              ▼
┌─────────────────────────────────────────────────────┐
│          depguard-reach library                      │
│  ┌──────────────────────────────────────────────┐   │
│  │  reach.Scan(repoPath, findings[])             │   │
│  │                                              │   │
│  │  1. Language detection                       │   │
│  │  2. Partition findings by ecosystem          │   │
│  │  3. Launch Semgrep concurrently (Tier 6)     │   │
│  │  4. runLanguageSlice() per ecosystem         │   │
│  │     ┌────────────────────────────────────┐  │   │
│  │     │ Tier 0: OSV ecosystem symbols       │  │   │
│  │     │ Tier 1: Fix-commit diff extract     │  │   │
│  │     │ Tier 2: CWE → sink catalogue        │  │   │
│  │     │         → Deep Analyzer (Joern/     │  │   │
│  │     │           govulncheck/PyCG)         │  │   │
│  │     │ Tier 3: Static import grep          │  │   │
│  │     │ Tier 4: Lockfile depth enrich       │  │   │
│  │     │ Tier 6: Semgrep OWASP rules         │  │   │
│  │     └────────────────────────────────────┘  │   │
│  │  5. Merge → emit CycloneDX VEX 1.7          │   │
│  └──────────────────────────────────────────────┘   │
└─────────────────────────────────────────────────────┘`}</pre>

            {/* Tier pipeline */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>The 6-Tier Pipeline</h3>
            <p style={{ lineHeight: 1.7, marginBottom: '0.75rem' }}>
              For each CVE finding, the pipeline tries tiers in order. The first tier that produces function-level evidence runs the deep analyzer (Joern/govulncheck/PyCG).
              If the deep analyzer returns <code>under_investigation</code>, the next tier is tried as a fallback.
            </p>
            <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead>
                  <tr>
                    <th>Tier</th><th>Name</th><th>What it does</th><th>Coverage</th><th>Confidence</th>
                    <th>Badge</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>0</strong></td>
                    <td>OSV Symbols</td>
                    <td>Reads function-level data directly from the OSV advisory (ecosystem_specific.symbols)</td>
                    <td>~40% of CVEs (Go: ~90%)</td>
                    <td style={{ color: '#10b981' }}>High</td>
                    <td><span style={{ background: '#10b981', color: '#fff', borderRadius: '4px', padding: '1px 6px', fontSize: '0.7rem' }}>osv-symbols</span></td>
                  </tr>
                  <tr>
                    <td><strong>1</strong></td>
                    <td>Fix-Commit</td>
                    <td>Fetches the GitHub fix commit diff and parses changed function names</td>
                    <td>+20% beyond Tier 0</td>
                    <td style={{ color: '#3b82f6' }}>High</td>
                    <td><span style={{ background: '#3b82f6', color: '#fff', borderRadius: '4px', padding: '1px 6px', fontSize: '0.7rem' }}>fix-commit</span></td>
                  </tr>
                  <tr>
                    <td><strong>2</strong></td>
                    <td>CWE Catalogue</td>
                    <td>Maps CWE type to 17 vulnerability classes × 4 ecosystems of known sink functions</td>
                    <td>+25% beyond Tiers 0–1</td>
                    <td style={{ color: '#f59e0b' }}>Medium</td>
                    <td><span style={{ background: '#f59e0b', color: '#fff', borderRadius: '4px', padding: '1px 6px', fontSize: '0.7rem' }}>cwe-patterns</span></td>
                  </tr>
                  <tr>
                    <td><strong>3</strong></td>
                    <td>Static Import Grep</td>
                    <td>Checks if the package is imported anywhere in the codebase; if not imported → not_affected (high)</td>
                    <td>100% coverage (fallback)</td>
                    <td style={{ color: '#8b5cf6' }}>High/Medium</td>
                    <td><span style={{ background: '#8b5cf6', color: '#fff', borderRadius: '4px', padding: '1px 6px', fontSize: '0.7rem' }}>import-check</span></td>
                  </tr>
                  <tr>
                    <td><strong>4</strong></td>
                    <td>Lockfile Depth</td>
                    <td>Reads lock files to determine direct vs. transitive; transitive + not imported → not_affected (high)</td>
                    <td>Enriches Tier 3</td>
                    <td style={{ color: '#10b981' }}>High upgrade</td>
                    <td><em style={{ fontSize: '0.75rem' }}>enriches import-check</em></td>
                  </tr>
                  <tr>
                    <td><strong>6</strong></td>
                    <td>Semgrep OWASP</td>
                    <td>Runs 500+ human-authored OWASP/CVE rules once for the whole repo; matches by CVE ID, CWE ID, or package name</td>
                    <td>~200–300 major CVEs with dedicated rules</td>
                    <td style={{ color: '#ef4444' }}>Medium</td>
                    <td><span style={{ background: '#ef4444', color: '#fff', borderRadius: '4px', padding: '1px 6px', fontSize: '0.7rem' }}>semgrep</span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Decision flow */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Tier Decision Flow</h3>
            <pre style={{
              background: 'var(--bs-tertiary-bg)', borderRadius: '8px', padding: '1rem 1.25rem',
              fontSize: '0.72rem', lineHeight: 1.7, overflowX: 'auto', marginBottom: '1.25rem',
              fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
            }}>{`CVE finding (OSVID, PackageName, Ecosystem)
       │
       ▼
Tier 0: OSV has function symbols?
  YES → Deep Analyzer → reachable / not_affected (high conf.) ──────────┐
  NO  ─────────────────────────────────────────────────────────────────►│
       │                                                                 │
       ▼                                                                 │
Tier 1: Fix commit parseable from GitHub?                               │
  YES → Deep Analyzer → reachable / not_affected (high conf.) ──────────┤
  NO  ─────────────────────────────────────────────────────────────────►│
       │                                                                 │
       ▼                                                                 │
Tier 2: Advisory has CWE in catalogue?                                  │
  YES → Deep Analyzer → reachable / not_affected (medium conf.) ─────────┤
  NO  ─────────────────────────────────────────────────────────────────►│
       │                                                                 │
       ▼                                                                 │
Tier 3: Package imported in any source file?                            │
  NO  → not_affected (HIGH — package not used in code) ─────────────────┤
  YES → vulnerable patterns found?                                      │
         YES → reachable (medium)  ─────────────────────────────────────┤
         NO  → not_affected (medium) ───────────────────────────────────┤
       │                                                                 │
       ▼ (Tier 4 enriches Tier 3 result)                                │
Tier 4: Package is transitive in lock file?                             │
  YES → upgrade to not_affected (HIGH) ─────────────────────────────────┤
       │                                                                 │
       ▼ (Tier 6 runs in parallel, used as final fallback)              │
Tier 6: Semgrep matched CVE/CWE/package rule?                           │
  YES → reachable (medium) ─────────────────────────────────────────────┤
  NO  → previous tier result stands                                     │
                                                                        ▼
                                           ScanResult + CycloneDX VEX 1.7`}</pre>

            {/* Tool stack */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Tool Stack (100% Open Source)</h3>
            <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead>
                  <tr><th>Language</th><th>Deep Analyzer</th><th>Algorithm</th><th>License</th><th>Quality</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Go</strong></td>
                    <td><code>govulncheck</code></td>
                    <td>VTA (Variable Type Analysis)</td>
                    <td>BSD-3-Clause</td>
                    <td>⭐⭐⭐⭐⭐ Go vuln DB has function-level data per advisory</td>
                  </tr>
                  <tr>
                    <td><strong>JavaScript / TypeScript</strong></td>
                    <td><code>Joern CPG</code></td>
                    <td>Code Property Graph traversal</td>
                    <td>Apache-2.0</td>
                    <td>⭐⭐⭐⭐ Inter-procedural taint flow across files</td>
                  </tr>
                  <tr>
                    <td><strong>Python</strong></td>
                    <td><code>PyCG</code></td>
                    <td>99.2% precision call graph</td>
                    <td>Apache-2.0</td>
                    <td>⭐⭐⭐⭐ Handles closures, decorators, generators</td>
                  </tr>
                  <tr>
                    <td><strong>Java</strong></td>
                    <td><code>Joern CPG</code> (source)</td>
                    <td>CPG with Java-specific sources</td>
                    <td>Apache-2.0</td>
                    <td>⭐⭐⭐⭐ No compilation needed — works on raw source</td>
                  </tr>
                  <tr>
                    <td><strong>All languages</strong></td>
                    <td><code>Semgrep</code> (Tier 6)</td>
                    <td>OWASP community rules — 500+ patterns</td>
                    <td>LGPL-2.1</td>
                    <td>⭐⭐⭐ Human-authored CVE/CWE triggers</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Joern CPG box */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Joern Code Property Graph (CPG)</h3>
            <p style={{ lineHeight: 1.7, marginBottom: '0.75rem' }}>
              Joern combines three graph representations into a single queryable structure:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
              {[
                { title: 'AST', sub: 'Abstract Syntax Tree', desc: 'Code structure — what the code says' },
                { title: 'CFG', sub: 'Control Flow Graph', desc: 'Execution order — which paths can run' },
                { title: 'PDG', sub: 'Program Dependence Graph', desc: 'Data flow — how values propagate' },
              ].map(({ title, sub, desc }) => (
                <div key={title} style={{ background: 'var(--bs-tertiary-bg)', borderRadius: '8px', padding: '0.75rem', fontSize: '0.8rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--color-accent, #6366f1)' }}>{title}</div>
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{sub}</div>
                  <div style={{ color: 'var(--bs-secondary-color)' }}>{desc}</div>
                </div>
              ))}
            </div>
            <p style={{ lineHeight: 1.7, marginBottom: '1rem' }}>
              <strong>CPG caching:</strong> The CPG build takes 10–20 minutes for large repos. Once built, it is cached at{' '}
              <code>~/.depguard-reach/cpg/</code>. All subsequent scans complete in <strong>seconds</strong>.
              Configure with <code>DEPGUARD_REACH_JOERN_BIN</code> and <code>DEPGUARD_REACH_CACHE_DIR</code>.
            </p>

            {/* Semgrep tier deep dive */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Tier 6: Semgrep — How CVE Rules Work</h3>
            <p style={{ lineHeight: 1.7, marginBottom: '0.75rem' }}>
              Semgrep CVE rules are <strong>usage pattern rules</strong> — they don't look inside libraries, they check if{' '}
              <em>your application code</em> calls the library's API in the dangerous way that triggers the CVE.
            </p>
            <div style={{
              background: 'var(--bs-tertiary-bg)', borderRadius: '8px', padding: '1rem 1.25rem',
              marginBottom: '1rem', fontSize: '0.8rem',
            }}>
              <strong>Example: Log4Shell (CVE-2021-44228)</strong><br /><br />
              The bug is inside <code>MessagePatternConverter.java</code> in the Log4j library JAR.
              But it's only triggered when YOUR code calls <code>logger.info(userInput)</code>.<br /><br />
              Semgrep's Log4Shell rule finds exactly that pattern in your source — a human security engineer
              encoded this knowledge when they wrote the rule.
              <pre style={{ margin: '0.5rem 0 0', fontSize: '0.72rem', background: 'none', padding: 0 }}>{`# Semgrep rule encodes: "if you import log4j AND pass user input to logger → CVE-2021-44228"
pattern: $LOG.info(..., $INPUT, ...)
pattern-inside: $INPUT = $REQ.getParameter(...); ...`}</pre>
            </div>
            <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead><tr><th>Scenario</th><th>Semgrep detects?</th><th>Why</th></tr></thead>
                <tbody>
                  <tr>
                    <td>High-profile CVE (Log4Shell, Spring4Shell, Struts)</td>
                    <td>✅ Yes — dedicated CVE rule</td>
                    <td>Human-authored rule for this exact CVE, matches your direct API call</td>
                  </tr>
                  <tr>
                    <td>CVE triggered by calling library's public API</td>
                    <td>✅ Yes — CWE-based OWASP rule</td>
                    <td>OWASP rules cover the vulnerability class (SQL injection, XSS, etc.)</td>
                  </tr>
                  <tr>
                    <td>CVE buried 2 hops deep in a transitive JAR</td>
                    <td>❌ No</td>
                    <td>Semgrep cannot inspect JAR internals — Tier 4 lockfile covers this case</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* VPA */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Vulnerable Path Analysis (VPA)</h3>
            <p style={{ lineHeight: 1.7, marginBottom: '0.75rem' }}>
              When Joern or govulncheck confirms a CVE is reachable, it traces the complete call chain from the
              user-controlled entry point to the vulnerable sink. This chain is the <strong>Vulnerable Path Analysis</strong> — the
              proof that the CVE is exploitable.
            </p>
            <pre style={{
              background: 'var(--bs-tertiary-bg)', borderRadius: '8px', padding: '1rem 1.25rem',
              fontSize: '0.72rem', lineHeight: 1.7, overflowX: 'auto', marginBottom: '1rem',
              fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
            }}>{`Entry: HTTP handler → doFilter()
    │
    ├─ calls: UserController.login()          ← your code
    │         │
    │         └─ calls: JdbcTemplate.query(
    │                     "SELECT * FROM users WHERE name='" + input + "'"
    │                   )
    │                   ▲
    │         Vulnerable sink — SQL Injection (CVE-XXXX-YYYY)
    │
VEX state: exploitable
VPA in API: vulnerablePathAnalysis: [
  { "caller": "doFilter",              "callee": "login"      },
  { "caller": "login",                 "callee": "query"      },
  { "caller": "JdbcTemplate.query",   "callee": "executeSQL" }
]`}</pre>
            <p style={{ lineHeight: 1.7, marginBottom: '1.25rem' }}>
              In the DepGuard portal, clicking a <code>reachable</code> finding opens the <strong>VPA Panel</strong> — an interactive
              call graph showing every hop from source to sink, with file and line number for each.
            </p>

            {/* VEX output */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>VEX Output (CycloneDX 1.7 — EU CRA Mandated)</h3>
            <p style={{ lineHeight: 1.7, marginBottom: '0.75rem' }}>
              Every reachability scan automatically produces a <strong>CycloneDX VEX 1.7 document</strong> (ECMA-424).
              EU CRA Article 13.6 requires VEX documents per product. DepGuard generates them automatically.
            </p>
            <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead><tr><th>Reachability</th><th>Confidence</th><th>VEX State</th><th>VEX Justification</th></tr></thead>
                <tbody>
                  <tr>
                    <td><code style={{ color: '#ef4444' }}>reachable</code></td>
                    <td>Any</td>
                    <td><code>exploitable</code></td>
                    <td>— (requires human confirmation)</td>
                  </tr>
                  <tr>
                    <td><code style={{ color: '#10b981' }}>not_affected</code></td>
                    <td>High</td>
                    <td><code>not_affected</code></td>
                    <td><code>vulnerable_code_not_present</code></td>
                  </tr>
                  <tr>
                    <td><code style={{ color: '#10b981' }}>not_affected</code></td>
                    <td>Medium</td>
                    <td><code>not_affected</code></td>
                    <td><code>vulnerable_code_not_in_execute_path</code></td>
                  </tr>
                  <tr>
                    <td><code style={{ color: '#f59e0b' }}>under_investigation</code></td>
                    <td>Unknown</td>
                    <td><code>under_investigation</code></td>
                    <td>—</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Confidence table */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>How Confidence is Calculated</h3>
            <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead><tr><th>Scenario</th><th>Confidence</th><th>Rationale</th></tr></thead>
                <tbody>
                  <tr><td>govulncheck/Joern confirms call path exists</td><td style={{ color: '#10b981' }}>High</td><td>Exhaustive call graph evidence</td></tr>
                  <tr><td>govulncheck/Joern confirms NOT in call graph</td><td style={{ color: '#10b981' }}>High</td><td>Exhaustive static analysis</td></tr>
                  <tr><td>Semgrep CVE/CWE rule matched</td><td style={{ color: '#f59e0b' }}>Medium</td><td>Human-authored rule — no full path proof</td></tr>
                  <tr><td>CWE sink patterns found in imported code</td><td style={{ color: '#f59e0b' }}>Medium</td><td>Heuristic pattern matching</td></tr>
                  <tr><td>Package imported but no vuln patterns found</td><td style={{ color: '#f59e0b' }}>Medium</td><td>Structural absence, not call-graph proof</td></tr>
                  <tr><td>Package is TRANSITIVE + not imported</td><td style={{ color: '#10b981' }}>High → not_affected</td><td>Lock file + zero usage = definitively not reachable</td></tr>
                  <tr><td>Package NOT imported anywhere</td><td style={{ color: '#10b981' }}>High → not_affected</td><td>Zero usage = definitively not reachable</td></tr>
                  <tr><td>No source code / analyzer crashed</td><td style={{ color: '#6b7280' }}>Unknown → under_investigation</td><td>Cannot determine</td></tr>
                </tbody>
              </table>
            </div>

            {/* Commercial impact */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Commercial Impact</h3>
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead><tr><th>Dimension</th><th>Impact</th></tr></thead>
                <tbody>
                  <tr><td><strong>~70% fewer alerts to triage</strong></td><td>Only reachable CVEs require action — developers work on real risk</td></tr>
                  <tr><td><strong>EU CRA Art. 13.6 — automated VEX</strong></td><td>Penalties up to €15M; DepGuard generates CycloneDX VEX per scan</td></tr>
                  <tr><td><strong>CI/CD gate accuracy</strong></td><td>Policy gates no longer fail on unreachable transitive CVEs</td></tr>
                  <tr><td><strong>Competitive moat</strong></td><td>No other OSS SBOM platform has multi-language reachability</td></tr>
                  <tr><td><strong>Proof of exploitability</strong></td><td>VPA gives security teams the evidence to prioritise or waive CVEs</td></tr>
                </tbody>
              </table>
            </div>

            {/* Platform comparison */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Platform Comparison</h3>
            <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead>
                  <tr><th>Platform</th><th>Go</th><th>JS</th><th>Python</th><th>Java</th><th>Open Source</th><th>VEX Output</th><th>Price</th></tr>
                </thead>
                <tbody>
                  <tr style={{ background: 'rgba(16,185,129,0.06)' }}>
                    <td><strong>DepGuard</strong></td>
                    <td>✅</td><td>✅</td><td>✅</td><td>✅</td>
                    <td>✅ MIT</td><td>✅ CycloneDX 1.7</td><td style={{ color: '#10b981' }}><strong>Free</strong></td>
                  </tr>
                  <tr>
                    <td>Snyk</td>
                    <td>✅</td><td>✅</td><td>✅</td><td>✅</td>
                    <td>❌ Proprietary</td><td>❌</td><td>€48/dev/month</td>
                  </tr>
                  <tr>
                    <td>Endor Labs</td>
                    <td>✅</td><td>✅</td><td>✅</td><td>✅</td>
                    <td>❌ Proprietary</td><td>❌</td><td>€98/dev/month</td>
                  </tr>
                  <tr>
                    <td>Socket.dev</td>
                    <td>❌</td><td>✅</td><td>❌</td><td>❌</td>
                    <td>❌ Proprietary</td><td>❌</td><td>€25/dev/month</td>
                  </tr>
                  <tr>
                    <td>Dependency-Track</td>
                    <td>❌</td><td>❌</td><td>❌</td><td>❌</td>
                    <td>✅ Apache-2.0</td><td>✅ OpenVEX</td><td>Free</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Env vars */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Configuration</h3>
            <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead><tr><th>Environment Variable</th><th>Required</th><th>Default</th><th>Purpose</th></tr></thead>
                <tbody>
                  <tr><td><code>DEPGUARD_REACH_JOERN_BIN</code></td><td>No</td><td><code>joern</code> (PATH)</td><td>Path to Joern binary for Java + JS analysis</td></tr>
                  <tr><td><code>DEPGUARD_REACH_CACHE_DIR</code></td><td>No</td><td><code>~/.depguard-reach</code></td><td>CPG cache directory</td></tr>
                  <tr><td><code>GITHUB_TOKEN</code></td><td>No</td><td>—</td><td>Raises GitHub API rate limit 60→5000 req/hr (Tier 1)</td></tr>
                </tbody>
              </table>
            </div>

            <div style={{ background: 'var(--bs-tertiary-bg)', borderRadius: '8px', padding: '1rem 1.25rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
              <strong>📄 Full documentation:</strong>{' '}
              <code>P20604-depguard-reach/docs/REACHABILITY.md</code> — Architecture, tier pipeline, algorithms, VEX mapping, API reference<br/>
              <code>P20604-depguard-reach/docs/FUNCTIONAL.md</code> — User-facing functional specification and use cases<br/>
              <code>P20604-depguard-reach/docs/TECHNICAL.md</code> — Low-level implementation details
            </div>

            <Pills terms={['Reachability Analysis', 'Vulnerable Path Analysis', 'VPA', 'Call Graph', 'CPG', 'VEX', 'CycloneDX VEX 1.7', 'ECMA-424', 'govulncheck', 'Joern', 'PyCG', 'Semgrep', 'VTA Algorithm', 'CWE Catalogue', 'Fix-Commit', 'Lockfile Depth', 'OSV Symbols', 'EU CRA Art.13.6', 'Alert Fatigue', 'False Positive', 'Air-gapped', 'Log4Shell', 'Spring4Shell']} />
          </section>

          <section id="slsa" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🔐" title={t('docs.sections.slsa')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.slsa.intro', 'SLSA provenance verification adds a build-trust layer on top of SBOM analysis.')}
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              DepGuard&apos;s MVP lets you upload one provenance attestation per SBOM in the repository detail view. The
              backend accepts both <strong>bare in-toto statements</strong> and <strong>DSSE envelopes</strong>, checks
              that the attestation is an SLSA provenance statement, extracts builder/build/source/material metadata,
              and compares the declared subject digest against the stored SBOM artifact. When a preserved original
              CycloneDX file exists, that artifact is also considered during digest matching.
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li>Upload a provenance attestation directly from the new <strong>SLSA</strong> tab</li>
              <li>See verification status: <strong>verified</strong>, <strong>mismatch</strong>, or <strong>incomplete</strong></li>
              <li>Review builder ID, build type, invocation ID, source URI/digest, materials count, and matched artifact</li>
              <li>Inspect verified checks, current limitations, and an <strong>estimated SLSA level</strong> derived from attestation structure</li>
            </ul>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              This is intentionally a conservative first release. DepGuard surfaces when signatures are present, but it
              does <strong>not yet</strong> perform full cryptographic signature trust verification or Sigstore/cosign-style
              policy enforcement. The UI calls out that limitation explicitly so the result is useful without overclaiming.
            </p>
            <WhyItMatters>
              {t('docs.content.slsa.whyItMatters', 'For regulated buyers and internal auditors, an SBOM alone answers only what is in the artifact.')}
            </WhyItMatters>
            <Pills terms={['SLSA', 'Provenance', 'DSSE', 'in-toto', 'Subject digest', 'Builder ID', 'Build type', 'Artifact integrity', 'EU CRA', 'EO 14028']} />
          </section>

          {/* ═══ Domain: 🏛️ Compliance & Governance ═══ */}
          <div id="domain-compliance" style={{
            borderTop: '2px solid var(--bs-border-color)',
            margin: '2rem 0 2rem',
            paddingTop: '1.5rem',
          }}>
            <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'var(--bs-secondary-color)',
              marginBottom: '0.25rem',
            }}>
              🏛️ Compliance & Governance
            </div>
          </div>

          <section id="compliance-fw" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🏛️" title={t('docs.sections.compliance-fw')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.compliance-fw.intro', 'The Compliance tab maps every CVE found in your SBOM to the specific controls in major compliance frameworks.')}
            </p>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              <thead><tr><th>Framework</th><th>Scope</th><th>Key controls mapped</th></tr></thead>
              <tbody>
                <tr>
                  <td>🇺🇸 <strong>NIST SP 800-53 Rev 5</strong></td>
                  <td>US Federal / general enterprise</td>
                  <td>SI-2, SI-3, RA-5, SA-11, CM-3, SA-15, SC-28</td>
                </tr>
                <tr>
                  <td>🌐 <strong>ISO/IEC 27001:2022</strong></td>
                  <td>International / certification</td>
                  <td>A.8.8, A.8.29, A.8.30, A.5.23, A.8.9</td>
                </tr>
                <tr>
                  <td>🇪🇺 <strong>EU Cyber Resilience Act 2024</strong></td>
                  <td>Products sold in the EU market</td>
                  <td>Art.13, Art.14, Art.15, Art.20, Art.24</td>
                </tr>
              </tbody>
            </table>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The mapping is severity-based:
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li><strong>All severities</strong> → vulnerability monitoring and flaw remediation controls</li>
              <li><strong>Critical &amp; High</strong> → additionally require developer testing, configuration
                change control, and (for EU CRA) active reporting obligations</li>
              <li><strong>Critical only</strong> → supply chain vetting controls (NIST SA-15, SC-28)</li>
            </ul>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The view shows:
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li><strong>Affected Controls summary</strong> — cards showing which controls are violated and
                how many CVEs trigger each one. Click a card to expand the full control description.</li>
              <li><strong>CVE → Control mapping table</strong> — every vulnerability with its mapped controls,
                filterable by severity. Badge <code>SI-2</code> links to the control description on hover.</li>
            </ul>

            {/* ── EU CRA Article 20 Compliance Report ── */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              🇪🇺 EU CRA Article 20 Compliance Report
            </h3>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Beyond CVE-to-control mapping, DepGuard provides a <strong>dedicated EU CRA Article 20 compliance audit</strong>{' '}
              for every SBOM. Article 20 of the Cyber Resilience Act specifies the mandatory technical documentation that
              manufacturers must produce and retain — including a machine-readable SBOM covering 11 distinct evidence fields.
              DepGuard checks all 11 and produces a scored, graded report exportable as a formal HTML audit document.
            </p>

            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '0.5rem' }}>
              The 11 Article 20 Checks
            </h4>
            <table className="table table-sm" style={{ fontSize: '0.8125rem', marginBottom: '1.25rem' }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Check</th>
                  <th>Article reference</th>
                  <th>Weight</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>Component names present</td><td>Art. 20(1)(a)</td><td>10</td></tr>
                <tr><td>2</td><td>Component versions present</td><td>Art. 20(1)(a)</td><td>10</td></tr>
                <tr><td>3</td><td>PURL coverage ≥ 80 %</td><td>Art. 20(1)(a)</td><td>12</td></tr>
                <tr><td>4</td><td>Supplier / originator coverage ≥ 50 %</td><td>Art. 20(1)(b)</td><td>10</td></tr>
                <tr><td>5</td><td>Cryptographic hash coverage ≥ 80 %</td><td>Art. 20(1)(c)</td><td>12</td></tr>
                <tr><td>6</td><td>Strong hash algorithm (SHA-256+)</td><td>Art. 20(1)(c)</td><td>8</td></tr>
                <tr><td>7</td><td>Dependency relationships declared</td><td>Art. 20(1)(d)</td><td>10</td></tr>
                <tr><td>8</td><td><strong>License declarations present</strong> <span style={{ color: '#6b7280', fontWeight: 400 }}>(new)</span></td><td>Art. 20(1)(f)</td><td>8</td></tr>
                <tr><td>9</td><td>Timestamp / creation date present</td><td>Art. 20(1)(e)</td><td>10</td></tr>
                <tr><td>10</td><td>Authors / tools that generated the SBOM</td><td>Art. 20(2)</td><td>5</td></tr>
                <tr><td>11</td><td>Manufacturer / supplier identity</td><td>Art. 20(1)(b)</td><td>5</td></tr>
              </tbody>
            </table>

            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '0.5rem' }}>
              Scoring &amp; Grading
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Each check contributes its weight to a <strong>0–100 compliance score</strong>. The score is translated into a
              letter grade using the same A–F scale as SBOM quality:
            </p>
            <table className="table table-sm" style={{ fontSize: '0.8125rem', width: 'auto', marginBottom: '1.25rem' }}>
              <thead><tr><th>Grade</th><th>Score</th><th>Interpretation</th></tr></thead>
              <tbody>
                <tr><td><strong style={{ color: '#10b981' }}>A</strong></td><td>90–100</td><td>Audit-ready; no material gaps</td></tr>
                <tr><td><strong style={{ color: '#84cc16' }}>B</strong></td><td>75–89</td><td>Minor gaps; remediation recommended</td></tr>
                <tr><td><strong style={{ color: '#f59e0b' }}>C</strong></td><td>60–74</td><td>Significant gaps; not suitable for submission</td></tr>
                <tr><td><strong style={{ color: '#f97316' }}>D</strong></td><td>40–59</td><td>Major compliance shortfall</td></tr>
                <tr><td><strong style={{ color: '#ef4444' }}>F</strong></td><td>0–39</td><td>Non-compliant; high penalty risk</td></tr>
              </tbody>
            </table>

            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '0.5rem' }}>
              Formal HTML Audit Report
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The downloadable HTML report is structured as a formal audit document with five sections:
            </p>
            <ol style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li><strong>Compliance Score</strong> — headline score, grade badge, and assessed date</li>
              <li><strong>Article 20 Checks</strong> — all 11 checks with pass/fail status and progress bars showing coverage percentages</li>
              <li><strong>Findings</strong> — detailed list of every gap including affected component count and evidence</li>
              <li><strong>Remediation Roadmap</strong> — gap checks sorted by weight (highest-impact first) with actionable fix guidance</li>
              <li><strong>Attestation &amp; Signature Block</strong> — space for responsible-person sign-off and date, suitable for inclusion in technical documentation dossiers</li>
            </ol>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Access the CRA report from <strong>Repo Details → Compliance → CRA tab</strong>.
            </p>

            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '0.5rem' }}>
              API Endpoints
            </h4>
            <pre style={{ background: 'var(--bs-tertiary-bg)', padding: '0.75rem', borderRadius: '6px', fontSize: '0.8rem' }}>
{`# Framework mapping (CVE → control)
GET /api/v1/sbom/:repo/:sbom/compliance?framework=nist
GET /api/v1/sbom/:repo/:sbom/compliance?framework=iso27001
GET /api/v1/sbom/:repo/:sbom/compliance?framework=cra

# EU CRA Article 20 dedicated report
GET /api/v1/sbom/{repo}/{sbom}/cra          → JSON compliance report
GET /api/v1/sbom/{repo}/{sbom}/cra/report   → downloadable HTML audit document`}
            </pre>

            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '0.5rem' }}>
              Policy Enforcement
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The <strong>Policy</strong> tab can enforce CRA readiness in CI/CD pipelines. Built-in presets and custom policies can
              gate on minimum CRA score, minimum CRA grade, required timestamp, required dependency relationships, and maximum
              missing percentages for PURLs, suppliers, license declarations, and cryptographic hashes.
            </p>
            <WhyItMatters>
              Penalties under EU CRA Article 20 can reach <strong>€15 million or 2.5 % of global annual turnover</strong>.
              A scored, graded, and downloadable audit report turns CRA compliance from a paper exercise into a verifiable,
              repeatable engineering workflow.
            </WhyItMatters>
            <Pills terms={['NIST SP 800-53', 'ISO 27001', 'EU CRA', 'Art.20', 'SI-2', 'RA-5', 'A.8.8', 'PURL', 'SHA-256', 'License declaration', 'Audit report', 'GRC', 'Compliance mapping', 'Audit evidence', 'Remediation roadmap', 'Attestation']} />
          </section>

          <section id="matrix" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="📊" title={t('docs.sections.matrix')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.matrix.intro', 'The Compliance Matrix provides a single-page portfolio overview of all your repositories, ranked by risk.')}
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Each row in the matrix combines four data sources:
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li>
                <strong>Vulnerability counts</strong> — broken down by severity (Critical / High / Medium / Low),
                sourced directly from DepGuard's OSV scan results.
              </li>
              <li>
                <strong>SBOM quality grade</strong> (A–F) — based on the five-dimension quality engine: completeness,
                identifiers, license quality, metadata, and version completeness.
              </li>
              <li>
                <strong>NTIA compliance</strong> — whether the SBOM contains the seven minimum NTIA elements required
                for US federal software procurement.
              </li>
              <li>
                <strong>Policy status</strong> — the result of the most recent policy evaluation (Pass / Fail) and
                the score percentage.
              </li>
              <li>
                <strong>CRA score and grade</strong> — the latest CRA compliance audit score for each repository, visible
                directly in the matrix and exportable via CSV for portfolio reporting.
              </li>
            </ul>

            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Risk Score
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              DepGuard calculates a composite <strong>risk score (0–100)</strong> for each repository using a weighted
              formula:
            </p>
            <div style={{ background: 'var(--bs-tertiary-bg)', borderRadius: 'var(--radius-sm)', padding: '0.875rem 1.125rem', fontFamily: 'monospace', fontSize: '0.8125rem', marginBottom: '1rem' }}>
              risk = (Critical × 10) + (High × 3) + (Medium × 1) + (Low × 0.2)<br />
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;+ (100 − qualityScore) × 0.25<br />
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;+ 15 if policy failed<br />
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(capped at 100)
            </div>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              This gives Critical vulnerabilities significantly more weight than Low ones, and penalizes poor SBOM
              quality and failed policy checks.
            </p>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
              <thead>
                <tr>
                  <th>Risk Level</th>
                  <th>Score Range</th>
                  <th>Meaning</th>
                </tr>
              </thead>
              <tbody>
                <tr><td><span style={{ color: '#dc2626', fontWeight: 700 }}>🔴 Critical</span></td><td>≥ 70</td><td>Immediate action required — Critical CVEs or severe quality issues</td></tr>
                <tr><td><span style={{ color: '#ea580c', fontWeight: 700 }}>🟠 High</span></td><td>40–69</td><td>Significant risk — High CVEs or multiple Medium findings</td></tr>
                <tr><td><span style={{ color: '#d97706', fontWeight: 700 }}>🟡 Medium</span></td><td>20–39</td><td>Moderate risk — review and remediate when possible</td></tr>
                <tr><td><span style={{ color: '#16a34a', fontWeight: 700 }}>🟢 Low</span></td><td>&lt; 20</td><td>Acceptable risk — maintain hygiene and monitor trends</td></tr>
              </tbody>
            </table>

            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Filtering & Sorting
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The matrix supports filtering by risk level, SBOM grade, policy status, and <strong>CRA state</strong>
              (ready / at risk / missing), plus free-text search by repository name. All columns are sortable.
              Use the <strong>CSV export</strong> button to download the full matrix for use in management reports,
              spreadsheets, or audit evidence.
            </p>

            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              License Distribution Tab
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Switch to the <strong>⚖️ License Distribution</strong> tab for a portfolio-wide breakdown of license
              risk categories. The view shows a global distribution bar (percentage of Permissive / Weak Copyleft /
              Strong Copyleft / Unknown packages), per-repository stacked bars, and the top most-used licenses
              across your entire portfolio. This analysis runs entirely on local SBOM data — no external API calls —
              making it fast even for large portfolios.
            </p>

            <WhyItMatters>
              {t('docs.content.matrix.whyItMatters', 'In organizations with many repositories, it is impossible to manually review each project\'s security posture every sprint.')}
            </WhyItMatters>
            <Pills terms={['Risk score', 'Portfolio view', 'NTIA compliance', 'Policy status', 'SBOM grade', 'Audit evidence', 'EU CRA', 'Risk-based prioritization', 'CSV export']} />
          </section>

          {/* ═══ Domain: 📊 Risk Intelligence ═══ */}
          <div id="domain-risk" style={{
            borderTop: '2px solid var(--bs-border-color)',
            margin: '2rem 0 2rem',
            paddingTop: '1.5rem',
          }}>
            <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'var(--bs-secondary-color)',
              marginBottom: '0.25rem',
            }}>
              📊 Risk Intelligence
            </div>
          </div>

          <section id="health" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🏥" title={t('docs.sections.health')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.health.intro', 'Beyond vulnerabilities, DepGuard evaluates each package on multiple health dimensions to give you a holistic picture of your dependency risk:')}
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li><strong>OpenSSF Scorecard score (0–10):</strong> Automated checks for security practices including
                branch protection, code review, signed releases, dependency update tools, and CI/CD security.</li>
              <li><strong>License clarity:</strong> Whether the package has a clearly identified, machine-readable license.</li>
              <li><strong>Maintenance status:</strong> Packages with no release in 2+ years are flagged as
                <em> abandoned</em> — they will not receive security patches. When the GitHub repository is
                officially <strong>archived</strong>, this is detected directly via the GitHub API and shown
                as a <code>👻 Archived</code> badge with a human-readable reason (e.g., "OpenSSF Scorecard
                'Maintained' score: 1/10" or "Repository is archived on GitHub").</li>
              <li><strong>Known advisories:</strong> Count of security advisories beyond CVEs (e.g., malicious
                package flags, typosquatting alerts).</li>
              <li><strong>Dependency depth:</strong> Transitive dependency chains that may carry hidden risk.</li>
            </ul>
            <WhyItMatters>
              {t('docs.content.health.whyItMatters', 'A technically functional library may still be a significant liability if it is abandoned, has unclear licensing, or follows poor security hygiene.')}
            </WhyItMatters>
            <Pills terms={['OpenSSF Scorecard', 'Abandoned package', 'License risk', 'GPL', 'AGPL', 'SSPL', 'Malicious package', 'Dependency depth']} />
          </section>

          <section id="licenses" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="⚖️" title={t('docs.sections.licenses')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.licenses.intro', 'Every open-source component carries a license that governs how it can be used, modified, and redistributed.')}
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li>
                <strong>Permissive</strong> (MIT, Apache 2.0, BSD-2/3): Use freely in commercial and proprietary
                software. Attribution required. No obligation to share modifications.
              </li>
              <li>
                <strong>Weak copyleft</strong> (LGPL, MPL 2.0): Modifications to the library itself must be shared
                under the same license, but your application code can remain proprietary.
              </li>
              <li>
                <strong>Strong copyleft</strong> (GPL-2.0, GPL-3.0, AGPL-3.0): If you distribute software that
                incorporates GPL code, the entire combined work may need to be released under the GPL. AGPL extends
                this to network/SaaS deployments.
              </li>
              <li>
                <strong>Commercial-incompatible</strong> (SSPL, BUSL, Commons Clause): Restrict use in SaaS
                products or commercial settings. Often used by database and infrastructure vendors.
              </li>
            </ul>
            <WhyItMatters>
              {t('docs.content.licenses.whyItMatters', 'License violations can result in civil litigation, forced open-sourcing of proprietary source code, or injunctions preventing product distribution entirely.')}
            </WhyItMatters>
            <Pills terms={['SPDX license ID', 'Permissive', 'Copyleft', 'GPL', 'AGPL', 'LGPL', 'MIT', 'Apache 2.0', 'SSPL', 'BUSL', 'License compatibility']} />
          </section>

          <section id="license-risk" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🏷️" title={t('docs.sections.license-risk')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.license-risk.intro', 'DepGuard automatically classifies every package\'s license into one of four risk categories.')}
            </p>

            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              <thead>
                <tr>
                  <th style={{ width: '120px' }}>Badge</th>
                  <th style={{ width: '170px' }}>Category</th>
                  <th>Examples</th>
                  <th>Commercial risk</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span style={{ color: '#16a34a', fontWeight: 700 }}>🟢 Permissive</span></td>
                  <td>Low risk</td>
                  <td>MIT, Apache-2.0, BSD-2/3-Clause, ISC, 0BSD, Unlicense, CC0</td>
                  <td>✅ Safe to use in proprietary products. Attribution required.</td>
                </tr>
                <tr>
                  <td><span style={{ color: '#d97706', fontWeight: 700 }}>🟡 Weak Copyleft</span></td>
                  <td>Medium risk</td>
                  <td>LGPL-2.1, LGPL-3.0, MPL-2.0, EPL-1.0/2.0, CDDL-1.x</td>
                  <td>⚠️ Modifications to the library must be shared. Application code can stay proprietary.</td>
                </tr>
                <tr>
                  <td><span style={{ color: '#dc2626', fontWeight: 700 }}>🔴 Strong Copyleft</span></td>
                  <td>High risk</td>
                  <td>GPL-2.0, GPL-3.0, AGPL-3.0, SSPL-1.0, EUPL-1.2</td>
                  <td>🚫 Entire combined work may need to be released under the same license. AGPL also covers SaaS deployments.</td>
                </tr>
                <tr>
                  <td><span style={{ color: '#6b7280', fontWeight: 700 }}>⚪ Unknown</span></td>
                  <td>Unquantified risk</td>
                  <td>NOASSERTION, NONE, empty, or unrecognized SPDX IDs</td>
                  <td>❓ Legal exposure is unknown. Treat as a compliance gap requiring manual review.</td>
                </tr>
              </tbody>
            </table>

            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              SPDX Family Matching
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.license-risk.spdxFamily', 'SPDX identifiers can carry suffixes. DepGuard\'s policy engine performs SPDX family matching.')}
            </p>

            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              SBOM License Fallback
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.license-risk.fallback', 'When an external registry (deps.dev) returns no license data for a package, DepGuard falls back to the license declared inside the SBOM itself.')}
            </p>

            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              License Distribution (Portfolio View)
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.license-risk.distribution', 'The License Distribution tab in the Compliance Matrix shows a portfolio-wide breakdown of license categories across all analyzed repositories.')}
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li>Global distribution bar showing % of Permissive / Weak Copyleft / Strong Copyleft / Unknown packages</li>
              <li>Per-repository stacked bars for quick visual comparison</li>
              <li>Package counts per category and top-5 most common licenses</li>
            </ul>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.license-risk.localAnalysis', 'This view uses a fast local-only analysis path — no external API calls — making it suitable for large portfolios.')}
            </p>

            <WhyItMatters>
              License risk is often invisible until it becomes a legal problem. A single AGPL or GPL dependency
              that went unnoticed can force an organization to open-source proprietary code, or face injunctions
              blocking product distribution. The Unknown category is equally dangerous: "we don't know the license"
              is not a defensible compliance posture. By making license risk visible per-package and per-portfolio,
              DepGuard enables legal and engineering teams to proactively identify and remediate license exposure
              before it escalates. This is especially critical for products sold in the EU (CRA) or to US federal
              customers (EO 14028), where license provenance must be demonstrable.
            </WhyItMatters>
            <Pills terms={['License Risk', 'Strong Copyleft', 'Weak Copyleft', 'Unknown License', 'SPDX family match', 'License Distribution', 'SBOM license fallback', 'licenseConcluded', 'licenseDeclared']} />
          </section>

          <section id="trends" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="📈" title={t('docs.sections.trends')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.trends.intro', 'DepGuard tracks risk scores and vulnerability counts over time for each repository.')}
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Use the trend view to:
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li>Compare two SBOM snapshots (Diff view) to see exactly which packages were added, removed, or changed</li>
              <li>Identify when a specific vulnerability was introduced via a dependency upgrade</li>
              <li>Demonstrate security improvement over time to auditors or stakeholders</li>
              <li>Detect regressions — if your risk score suddenly spikes, the trend makes it immediately visible</li>
            </ul>
            <WhyItMatters>
              {t('docs.content.trends.whyItMatters', 'Security is not a point-in-time event. New vulnerabilities are discovered daily.')}
            </WhyItMatters>
            <Pills terms={['Risk score', 'Snapshot', 'Trend', 'Diff', 'Regression', 'Compliance over time']} />
          </section>

          <section id="supplier-trust" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🤝" title={t('docs.sections.supplier-trust')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The Supplier Trust Portfolio combines multiple risk signals per package into a single, portfolio-wide trust scorecard.
              Instead of reviewing malware, maintainer anomalies, reachability, and provenance separately for each package in each
              repository, the Supplier Trust view gives you one consolidated risk tier per dependency across your entire portfolio.
            </p>
            <WhyItMatters>
              EU CRA Article 13 requires manufacturers to exercise due diligence in their supply chain. Supplier Trust provides the
              exact evidence trail needed: a composite risk signal per open-source component that combines 5 independent risk dimensions
              into an auditable, exportable report.
            </WhyItMatters>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Risk Dimensions Combined
            </h4>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
              <thead><tr><th>Dimension</th><th>Signal</th></tr></thead>
              <tbody>
                <tr><td><strong>🦠 Malware / Typosquatting</strong></td><td>OSV MAL- match or typosquat detection (any algorithm)</td></tr>
                <tr><td><strong>👤 Maintainer Health</strong></td><td>Anomaly score from maintainer anomaly detection</td></tr>
                <tr><td><strong>👻 Abandoned Status</strong></td><td>OpenSSF Maintained score &lt; 3 or GitHub archived</td></tr>
                <tr><td><strong>🔍 Reachability Context</strong></td><td>Whether vulnerable functions are reachable in your code</td></tr>
                <tr><td><strong>🔐 SLSA Provenance</strong></td><td>Whether a signed, verified build attestation exists</td></tr>
              </tbody>
            </table>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Where to Find It
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Navigate to <strong>Supplier Trust</strong> in the sidebar. The view shows a portfolio-level snapshot with risk tiers
              (Critical / High / Medium / Low / Trusted) per package, sortable by any dimension. Use the CSV export for audit evidence.
            </p>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              API Endpoint
            </h4>
            <pre style={{ background: 'var(--bs-tertiary-bg)', padding: '0.75rem', borderRadius: '6px', fontSize: '0.8rem' }}>
{`GET /api/v1/portfolio/supplier-trust   → all packages ranked by composite trust score`}
            </pre>
            <Pills terms={['Supplier Trust', 'EU CRA Art.13', 'Supply chain due diligence', 'Malware', 'Maintainer anomaly', 'SLSA', 'Reachability', 'Abandoned', 'Trust tier', 'Portfolio']} />
          </section>

          {/* ═══ Domain: 📋 Policy & Remediation ═══ */}
          <div id="domain-policy" style={{
            borderTop: '2px solid var(--bs-border-color)',
            margin: '2rem 0 2rem',
            paddingTop: '1.5rem',
          }}>
            <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'var(--bs-secondary-color)',
              marginBottom: '0.25rem',
            }}>
              📋 Policy & Remediation
            </div>
          </div>

          <section id="policy" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="📋" title={t('docs.sections.policy')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.policy.intro', 'DepGuard\'s policy engine lets you define security gates that your SBOMs must pass before reaching production.')}
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li>No Critical CVEs allowed (CVSS ≥ 9.0)</li>
              <li>Maximum number of High severity CVEs</li>
              <li>Minimum required OpenSSF Scorecard score (e.g., ≥ 5.0)</li>
              <li>Banned licenses by SPDX ID (e.g., AGPL-3.0, SSPL) — with SPDX family matching for variants</li>
              <li>Banned license <em>categories</em> — block all Strong Copyleft, Weak Copyleft, or Unknown licenses in one rule</li>
              <li>Maximum allowed percentage of Unknown-license packages (e.g., no more than 10% undeclared licenses)</li>
              <li>Maximum allowed package age (e.g., no packages abandoned for more than 2 years)</li>
              <li>Minimum SBOM quality score (e.g., Grade B or above)</li>
            </ul>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.policy.presets', 'DepGuard ships with three preset policy profiles: Strict, Moderate, and Permissive.')}
            </p>
            <WhyItMatters>
              {t('docs.content.policy.whyItMatters', 'Manual review of every SBOM across every repository is impossible at scale.')}
            </WhyItMatters>
            <Pills terms={['Policy gate', 'PASS/FAIL', 'CI/CD integration', 'Preset policies', 'Risk threshold', 'Violation', 'Compliance score']} />
          </section>

          <section id="vex" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🔏" title={t('docs.sections.vex')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.vex.intro', 'VEX (Vulnerability Exploitability eXchange) solves one of the biggest pain points in vulnerability management.')}
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              DepGuard's VEX tab (within each SBOM's detail view) lets you annotate every CVE with an
              exploitability statement. Statements are stored in the database and linked to the specific
              repository and SBOM commit hash.
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              In addition to VEX statements, the tab also shows <strong>patch availability</strong> sourced from
              OSV.dev. When a fix exists, a <code>🔧 → v2.1.7</code> badge appears next to the package version,
              indicating the earliest version that resolves the CVE. A <strong>Patchable</strong> stat card at the top
              shows how many of your CVEs already have a fix available. Use the <strong>🔧 Patch beschikbaar</strong> filter
              button to focus only on CVEs that can be resolved by upgrading.
            </p>

            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              VEX Status Values
            </h4>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              <thead>
                <tr>
                  <th style={{ width: '160px' }}>Status</th>
                  <th>Meaning</th>
                  <th>When to use</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span style={{ color: '#dc2626', fontWeight: 700 }}>🔴 Exploitable</span></td>
                  <td>The CVE is exploitable in this product</td>
                  <td>Confirmed vulnerability — immediate remediation required</td>
                </tr>
                <tr>
                  <td><span style={{ color: '#d97706', fontWeight: 700 }}>🟡 In Triage</span></td>
                  <td>Under active investigation</td>
                  <td>Default starting state — assign to a team member for investigation</td>
                </tr>
                <tr>
                  <td><span style={{ color: '#2563eb', fontWeight: 700 }}>🔵 Under Investigation</span></td>
                  <td>Being analyzed but no conclusion yet</td>
                  <td>More complex analysis needed — mark to prevent duplicate effort</td>
                </tr>
                <tr>
                  <td><span style={{ color: '#16a34a', fontWeight: 700 }}>🟢 Not Exploitable</span></td>
                  <td>The CVE exists but cannot be exploited</td>
                  <td>Requires a <strong>justification</strong> (see below) as structured evidence</td>
                </tr>
                <tr>
                  <td><span style={{ color: '#059669', fontWeight: 700 }}>✅ Fixed</span></td>
                  <td>Vulnerability has been resolved</td>
                  <td>After upgrading the package or applying a patch</td>
                </tr>
              </tbody>
            </table>

            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Not Exploitable Justifications
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              When marking a CVE as <em>Not Exploitable</em>, you must provide a structured justification. These
              are standardized CycloneDX VEX justification codes:
            </p>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              <thead><tr><th>Justification</th><th>Meaning</th></tr></thead>
              <tbody>
                <tr><td><code>ComponentNotPresent</code></td><td>The component is included in the SBOM but not actually deployed or bundled in the product</td></tr>
                <tr><td><code>VulnerableCodeNotPresent</code></td><td>The package is included but the specific vulnerable code (function/class/module) is absent or excluded by build flags</td></tr>
                <tr><td><code>VulnerableCodeNotInExecutePath</code></td><td>The vulnerable code exists but can never be reached during normal execution (dead code)</td></tr>
                <tr><td><code>VulnerableCodeCannotBeControlledByAdversary</code></td><td>The vulnerability requires attacker input that this product never accepts (e.g., network input, file paths)</td></tr>
                <tr><td><code>InlineMitigationsAlreadyExist</code></td><td>Compensating controls are in place that prevent exploitation (WAF rule, sandboxing, input sanitization)</td></tr>
              </tbody>
            </table>

            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Regulatory Alignment
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              VEX is referenced in several key regulatory and standards frameworks:
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li><strong>EU CRA (Cyber Resilience Act)</strong> — requires manufacturers to address and disclose known vulnerabilities. VEX provides a structured, auditable format for doing so.</li>
              <li><strong>NIST SSDF (SP 800-218)</strong> — PS.3 requires tracking and addressing vulnerabilities. VEX statements provide machine-readable evidence of analysis.</li>
              <li><strong>CISA VEX guidance</strong> — CISA recommends VEX as the standard mechanism for conveying exploitability context alongside SBOMs.</li>
              <li><strong>OpenVEX / CycloneDX VEX</strong> — DepGuard uses CycloneDX-compatible status and justification vocabulary.</li>
            </ul>

            <WhyItMatters>
              {t('docs.content.vex.whyItMatters', 'Without VEX, every CVE match in an SBOM appears equally urgent — leading to vulnerability fatigue.')}
            </WhyItMatters>
            <Pills terms={['VEX', 'VEX Status', 'VEX Justification', 'Exploitable', 'Not Exploitable', 'CycloneDX VEX', 'OpenVEX', 'EU CRA', 'NIST SSDF', 'Vulnerability fatigue', 'CISA', 'FixedIn', 'Patchable']} />
          </section>

          <section id="remediation" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="📋" title={t('docs.sections.remediation')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.remediation.intro', 'Finding vulnerabilities is only half the job — tracking who is fixing them and by when is equally important.')}
            </p>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Status Workflow
            </h4>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              <thead><tr><th style={{ width: '160px' }}>Status</th><th>Meaning</th></tr></thead>
              <tbody>
                <tr>
                  <td><span style={{ color: '#6b7280', fontWeight: 700 }}>⚪ Untracked</span></td>
                  <td>CVE has not been assigned yet — appears in the Untracked filter</td>
                </tr>
                <tr>
                  <td><span style={{ color: '#d97706', fontWeight: 700 }}>🟡 Acknowledged</span></td>
                  <td>Team is aware of the CVE and has accepted responsibility</td>
                </tr>
                <tr>
                  <td><span style={{ color: '#2563eb', fontWeight: 700 }}>🔵 In Progress</span></td>
                  <td>Active work is underway (upgrade, patch, or workaround)</td>
                </tr>
                <tr>
                  <td><span style={{ color: '#16a34a', fontWeight: 700 }}>🟢 Resolved</span></td>
                  <td>The vulnerability has been addressed — clears the overdue flag</td>
                </tr>
                <tr>
                  <td><span style={{ color: '#7c3aed', fontWeight: 700 }}>🟣 Accepted Risk</span></td>
                  <td>Deliberate decision not to fix — requires documented notes and owner</td>
                </tr>
                <tr>
                  <td><span style={{ color: '#7c3aed', fontWeight: 700 }}>🫥 Suppressed</span></td>
                  <td>Finding is intentionally hidden from operational queues — requires a documented suppression reason</td>
                </tr>
              </tbody>
            </table>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Per-CVE fields
            </h4>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              <thead><tr><th>Field</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td><strong>Status</strong></td><td>Current workflow stage (see above)</td></tr>
                <tr><td><strong>Priority</strong></td><td>Critical / High / Medium / Low — independent of CVSS score, reflects business impact</td></tr>
                <tr><td><strong>Owner</strong></td><td>Free-text name or team responsible for the fix</td></tr>
                <tr><td><strong>Due Date</strong></td><td>Target resolution date — items past this date with status ≠ Resolved are flagged as <span style={{ color: '#dc2626', fontWeight: 600 }}>⏰ OVERDUE</span></td></tr>
                <tr><td><strong>Verification</strong></td><td>Tracks whether the fix is pending validation, verified, failed validation, or does not need further verification</td></tr>
                <tr><td><strong>Ticket / Reference</strong></td><td>Links the finding to Jira, PRs, or external evidence for auditability</td></tr>
                <tr><td><strong>Exception / Suppression Reason</strong></td><td>Required justification for Accepted Risk and Suppressed states</td></tr>
                <tr><td><strong>Notes</strong></td><td>Free-text field for context, decisions, workarounds, or links to PRs</td></tr>
                <tr><td><strong>FixedIn</strong></td><td>Suggested upgrade version from OSV.dev shown as <code>🔧 → v2.1.7</code></td></tr>
              </tbody>
            </table>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Stats Bar &amp; Filters
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The top of the tab shows live counters: <strong>Total CVEs · Tracked · In Progress · Resolved · Verified · Accepted Risk · Suppressed · Overdue · Unassigned Critical</strong>.
              The filters let you focus on lifecycle states such as Verified or Suppressed, while overdue items still stand out with a red border.
            </p>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Jira integration workflow
            </h4>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              <thead><tr><th style={{ width: '180px' }}>Step</th><th>What happens</th></tr></thead>
              <tbody>
                <tr><td><strong>1. Configure Jira</strong></td><td>Admins open <code>User Management → Jira</code> and provide the Jira base URL, user email, API token, project key, and issue type.</td></tr>
                <tr><td><strong>2. Track the finding</strong></td><td>A CVE must already have a remediation record before DepGuard will allow Jira issue creation.</td></tr>
                <tr><td><strong>3. Create Jira</strong></td><td>The Remediation tab adds a <code>Create Jira</code> action that opens an issue using the repo, package, severity, owner, due date, and fixed-version context.</td></tr>
                <tr><td><strong>4. Sync Jira</strong></td><td>Once linked, <code>Sync Jira</code> refreshes the live Jira status back onto the remediation record.</td></tr>
                <tr><td><strong>5. Audit trail</strong></td><td>DepGuard stores the Jira key, browse URL, current Jira status, last sync time, and remediation history so reviews stay audit-friendly.</td></tr>
              </tbody>
            </table>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Relevant API endpoints are <code>GET /integrations/jira/status</code>, <code>GET/PUT /integrations/jira/settings</code>,
              <code> POST /sbom/:repoName/:sbom/remediation/:vulnId/jira</code>, and
              <code> POST /sbom/:repoName/:sbom/remediation/:vulnId/jira/sync</code>.
            </p>
            <WhyItMatters>
              {t('docs.content.remediation.whyItMatters', 'Regulators increasingly require not just that you identify vulnerabilities, but that you can demonstrate a process for addressing them.')}
            </WhyItMatters>
            <Pills terms={['Remediation', 'Overdue', 'Accepted Risk', 'Suppressed', 'Verification', 'Owner', 'Due Date', 'FixedIn', 'EU CRA', 'NIST SSDF', 'Audit trail']} />
          </section>

          <section id="evidence-pack" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="📦" title={t('docs.sections.evidence-pack')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The Evidence Pack is a single downloadable ZIP archive that bundles all compliance artifacts for a specific SBOM into
              one auditor-ready package. It is designed for sharing with regulators, security auditors, or enterprise procurement teams
              that require evidence of software supply chain due diligence.
            </p>
            <WhyItMatters>
              EU CRA Article 20 requires manufacturers to retain technical documentation (including SBOMs) for 10 years after product
              placement on the EU market. The Evidence Pack creates a timestamped, manifest-verified archive that can be stored as
              a point-in-time compliance snapshot — making it significantly easier to respond to audit requests.
            </WhyItMatters>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Pack Contents
            </h4>
            <table className="table table-sm" style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
              <thead><tr><th>File</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td><code>sbom.json</code></td><td>The original SBOM file (SPDX-JSON or CycloneDX-JSON)</td></tr>
                <tr><td><code>cra-report.json</code></td><td>EU CRA Article 20 compliance report (all 11 checks, JSON format)</td></tr>
                <tr><td><code>cra-report.html</code></td><td>Formal HTML audit document with EU flag, scorecard, attestation block</td></tr>
                <tr><td><code>remediation.json</code></td><td>All remediation records: status, owner, due date, resolution history</td></tr>
                <tr><td><code>reachability.json</code></td><td>Reachability analysis results: which CVEs are reachable and via which tier</td></tr>
                <tr><td><code>slsa-provenance.json</code></td><td>SLSA attestation metadata and verification result (if uploaded)</td></tr>
                <tr><td><code>supplier-trust.json</code></td><td>Supplier trust snapshot: per-package composite trust score</td></tr>
                <tr><td><code>audit-log.json</code></td><td>Audit trail of all actions taken on this SBOM (actor, timestamp, IP)</td></tr>
                <tr><td><code>manifest.json</code></td><td>SHA-256 digests of all files in the pack — for tamper detection</td></tr>
              </tbody>
            </table>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              How to Download
            </h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              In <strong>Repo Details</strong>, select an SBOM from the dropdown and click the <strong>📦 Evidence Pack</strong> button
              in the top-right action bar. The pack is generated on demand and downloaded as a ZIP file named
              <code>{'{repo}-{sbom-hash}-evidence-pack.zip'}</code>.
            </p>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              API Endpoint
            </h4>
            <pre style={{ background: 'var(--bs-tertiary-bg)', padding: '0.75rem', borderRadius: '6px', fontSize: '0.8rem' }}>
{`GET /api/v1/sbom/:repo/:sbom/evidence-pack   → download ZIP (authenticated)`}
            </pre>
            <Pills terms={['Evidence Pack', 'EU CRA Art.20', 'Audit evidence', 'ZIP archive', 'SHA-256 manifest', 'SLSA', 'Remediation', 'Reachability', 'Supplier Trust', 'CRA report']} />
          </section>

          <section id="cicd" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="⚡" title={t('docs.sections.cicd')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.cicd.intro', 'DepGuard can automatically scan every commit by integrating with your CI/CD pipeline.')}
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Policy gates are no longer limited to CVEs and licenses. CRA-oriented controls are also available, so a pipeline
              can fail when an SBOM is missing mandatory CRA evidence such as timestamp, dependency relationships, supplier
              coverage, PURL coverage, or hashes.
            </p>
            <h4 style={{ fontSize: '1rem', fontWeight: 700, margin: '1.25rem 0 0.75rem' }}>How it works</h4>
            <ol style={{ lineHeight: 2, paddingLeft: '1.5rem', color: 'var(--bs-body-color)' }}>
              <li><strong>Generate SBOM</strong> — CycloneDX tooling scans your dependencies and produces a JSON bill of materials.</li>
              <li><strong>Upload to DepGuard</strong> — The SBOM is sent via <code>POST /sbom/:repo/upload</code> using a bearer token stored as a GitHub Secret.</li>
              <li><strong>Policy gate</strong> — DepGuard evaluates the SBOM against your chosen policy via <code>GET /sbom/:repo/:sbom/policy/:policy/gate</code>. Returns HTTP 200 on pass, HTTP 409 on failure → pipeline fails.</li>
              <li><strong>View results</strong> — Full vulnerability details, license analysis and trends are visible in the DepGuard dashboard.</li>
            </ol>
            <h4 style={{ fontSize: '1rem', fontWeight: 700, margin: '1.25rem 0 0.75rem' }}>Supported ecosystems</h4>
            <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
              <thead><tr><th>Ecosystem</th><th>SBOM tool used</th><th>File detected</th></tr></thead>
              <tbody>
                {[
                  ['Node.js / npm',  '@cyclonedx/cyclonedx-npm', 'package.json'],
                  ['Python / pip',   'cyclonedx-bom', 'requirements.txt'],
                  ['Go modules',     'CycloneDX gh-gomod-generate-sbom', 'go.mod'],
                  ['Java / Maven',   'cyclonedx-maven-plugin', 'pom.xml'],
                  ['Java / Gradle',  'cyclonedx-gradle-plugin', 'build.gradle'],
                  ['.NET / NuGet',   'CycloneDX dotnet tool', '.csproj / .sln'],
                ].map(([eco, tool, file]) => (
                  <tr key={eco}><td><strong>{eco}</strong></td><td><code>{tool}</code></td><td><code>{file}</code></td></tr>
                ))}
              </tbody>
            </table>
            <h4 style={{ fontSize: '1rem', fontWeight: 700, margin: '1.25rem 0 0.75rem' }}>Setup</h4>
            <ol style={{ lineHeight: 2, paddingLeft: '1.5rem', color: 'var(--bs-body-color)' }}>
              <li>Navigate to <strong>Settings &amp; AI → CI/CD Integration</strong> in the sidebar.</li>
              <li>Select your ecosystem and configure the repository name and policy.</li>
              <li>Download or copy the generated workflow YAML.</li>
              <li>Place it at <code>.github/workflows/depguard.yml</code> in your repository.</li>
              <li>Add a GitHub Secret named <code>DEPGUARD_TOKEN</code> containing a valid DepGuard JWT token.</li>
            </ol>
            <div style={{ background: 'var(--bs-tertiary-bg)', border: '1px solid var(--bs-border-color)', borderRadius: 8, padding: '0.875rem 1rem', marginTop: '1rem', fontSize: '0.875rem' }}>
              <strong>💡 Tip:</strong> The CI/CD gate endpoint is publicly documented in the{' '}
              <a href="/docs" style={{ color: 'var(--accent, #6366f1)' }}>Swagger UI</a> at{' '}
              <code>GET /sbom/:repoName/:sbom/policy/:policyId/gate</code>. It returns HTTP <code>200</code> on pass
              and HTTP <code>409</code> on failure, making it compatible with any CI system that checks exit codes.
            </div>
            <Pills terms={['GitHub Actions', 'CycloneDX', 'CI/CD gate', 'Policy', 'Bearer token', 'SBOM upload']} />
          </section>

          <section id="webhooks" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🔔" title={t('docs.sections.webhooks')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.webhooks.intro', 'DepGuard can notify your team in real-time via outgoing webhooks when a critical finding is detected.')}
            </p>

            <h5 style={{ marginBottom: 8, marginTop: 20 }}>Supported events</h5>
            <table className="doc-table" style={{ marginBottom: 20 }}>
              <thead>
                <tr><th>Event</th><th>When</th></tr>
              </thead>
              <tbody>
                <tr><td><code>sbom_uploaded</code></td><td>Each time a new SBOM file is processed</td></tr>
                <tr><td><code>policy_failed</code></td><td>CI/CD policy gate rejects the SBOM (HTTP 409)</td></tr>
                <tr><td><code>new_vulnerability</code></td><td>CVE found in an SBOM scan (filterable by severity)</td></tr>
                <tr><td><code>overdue_remediation</code></td><td>Deadline for a remediation item has passed</td></tr>
              </tbody>
            </table>

            {/* ── Platform setup guides ── */}
            <h5 style={{ marginBottom: 12, marginTop: 28 }}>Platform configuration</h5>

            {/* Slack */}
            <div style={{ background: 'var(--bs-tertiary-bg)', border: '1px solid var(--bs-border-color)', borderRadius: 10, padding: '18px 20px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: '1.4rem' }}>💬</span>
                <strong style={{ fontSize: '1rem', color: 'var(--bs-body-color)' }}>Slack — Incoming Webhook</strong>
              </div>
              <ol style={{ color: 'var(--bs-body-color)', lineHeight: 2, margin: 0, paddingLeft: 20 }}>
                <li>Go to <strong>api.slack.com/apps</strong> → click <strong>Create New App</strong> → choose <em>From scratch</em>.</li>
                <li>Give the app a name (e.g. <em>DepGuard Alerts</em>) and select your workspace.</li>
                <li>Click <strong>Incoming Webhooks</strong> in the left menu and toggle it <strong>On</strong>.</li>
                <li>Click <strong>Add New Webhook to Workspace</strong> at the bottom.</li>
                <li>Select the Slack channel to receive alerts (e.g. <code>#security-alerts</code>) and click <strong>Allow</strong>.</li>
                <li>Copy the Webhook URL — it starts with <code>https://hooks.slack.com/services/...</code>.</li>
                <li>Paste the URL into DepGuard → <strong>Settings &amp; AI → Webhooks</strong>, choose type <strong>Slack</strong>.</li>
              </ol>
              <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(99,102,241,0.08)', borderRadius: 7, fontSize: '0.83rem', color: 'var(--bs-secondary-color)' }}>
                💡 DepGuard sends a formatted <strong>Block Kit</strong> message with color-coding by severity (red = CRITICAL, orange = HIGH).
              </div>
            </div>

            {/* Microsoft Teams */}
            <div style={{ background: 'var(--bs-tertiary-bg)', border: '1px solid var(--bs-border-color)', borderRadius: 10, padding: '18px 20px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: '1.4rem' }}>🟦</span>
                <strong style={{ fontSize: '1rem', color: 'var(--bs-body-color)' }}>Microsoft Teams — Incoming Webhook connector</strong>
              </div>
              <ol style={{ color: 'var(--bs-body-color)', lineHeight: 2, margin: 0, paddingLeft: 20 }}>
                <li>Open Microsoft Teams and navigate to the channel that should receive alerts.</li>
                <li>Click the <strong>three dots (···)</strong> next to the channel name → <strong>Connectors</strong>.</li>
                <li>Search for <strong>Incoming Webhook</strong> and click <strong>Configure</strong>.</li>
                <li>Give the connector a name (e.g. <em>DepGuard</em>), optionally upload a logo, and click <strong>Create</strong>.</li>
                <li>Copy the generated Webhook URL — it ends with <code>/IncomingWebhook/...</code>.</li>
                <li>Click <strong>Done</strong> to save the connector.</li>
                <li>Paste the URL into DepGuard → <strong>Settings &amp; AI → Webhooks</strong>, choose type <strong>Microsoft Teams</strong>.</li>
              </ol>
              <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(99,102,241,0.08)', borderRadius: 7, fontSize: '0.83rem', color: 'var(--bs-secondary-color)' }}>
                💡 DepGuard sends a <strong>MessageCard</strong> with a theme color and structured summary. Microsoft is working on Adaptive Cards v2 — compatibility with new Teams versions is maintained.
              </div>
              <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(245,158,11,0.08)', borderRadius: 7, fontSize: '0.83rem', color: 'var(--bs-secondary-color)' }}>
                ⚠️ <strong>Note:</strong> Microsoft is deprecating Office 365 Connectors as of August 2026. Prefer <strong>Power Automate</strong> as an alternative (see below).
              </div>
            </div>

            {/* Power Automate */}
            <div style={{ background: 'var(--bs-tertiary-bg)', border: '1px solid var(--bs-border-color)', borderRadius: 10, padding: '18px 20px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: '1.4rem' }}>⚡</span>
                <strong style={{ fontSize: '1rem', color: 'var(--bs-body-color)' }}>Microsoft Power Automate (recommended for Teams)</strong>
              </div>
              <ol style={{ color: 'var(--bs-body-color)', lineHeight: 2, margin: 0, paddingLeft: 20 }}>
                <li>Go to <strong>make.powerautomate.com</strong> and create a new flow.</li>
                <li>Choose trigger: <strong>When a HTTP request is received</strong>.</li>
                <li>After saving, click the trigger step to copy the generated <strong>HTTP POST URL</strong>.</li>
                <li>Add an action: <strong>Post message in a chat or channel</strong> (Microsoft Teams connector).</li>
                <li>Use dynamic content (e.g. <code>body/data/vuln_id</code>, <code>body/data/severity</code>) to build the message.</li>
                <li>Paste the HTTP POST URL into DepGuard → Webhooks, choose type <strong>Generic</strong>.</li>
              </ol>
              <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(99,102,241,0.08)', borderRadius: 7, fontSize: '0.83rem', color: 'var(--bs-secondary-color)' }}>
                💡 With Power Automate you have full control over the message format and can add conditions (e.g. only alert a manager for CRITICAL findings).
              </div>
            </div>

            {/* Generic HTTP */}
            <div style={{ background: 'var(--bs-tertiary-bg)', border: '1px solid var(--bs-border-color)', borderRadius: 10, padding: '18px 20px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: '1.4rem' }}>🔗</span>
                <strong style={{ fontSize: '1rem', color: 'var(--bs-body-color)' }}>Generic HTTP — custom endpoint or tools like n8n / Zapier</strong>
              </div>
              <p style={{ color: 'var(--bs-body-color)', margin: '0 0 8px', lineHeight: 1.7, fontSize: '0.9rem' }}>
                Use the <strong>Generic</strong> type for any HTTP endpoint: custom backend, n8n, Zapier, Make (Integromat), PagerDuty, OpsGenie, etc.
                DepGuard POSTs a raw JSON payload to your URL.
              </p>
              <table className="doc-table" style={{ marginBottom: 8 }}>
                <thead><tr><th>Header</th><th>Value</th></tr></thead>
                <tbody>
                  <tr><td><code>Content-Type</code></td><td><code>application/json</code></td></tr>
                  <tr><td><code>X-DepGuard-Event</code></td><td>Event name, e.g. <code>new_vulnerability</code></td></tr>
                  <tr><td><code>X-DepGuard-Signature</code></td><td><code>sha256=&lt;hmac-hex&gt;</code> (only when secret is configured)</td></tr>
                  <tr><td><code>User-Agent</code></td><td><code>DepGuard-Webhook/1.0</code></td></tr>
                </tbody>
              </table>
              <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(99,102,241,0.08)', borderRadius: 7, fontSize: '0.83rem', color: 'var(--bs-secondary-color)' }}>
                💡 Endpoint must respond with HTTP 2xx within 10 seconds. All deliveries are logged in <strong>Webhooks → Log</strong>.
              </div>
            </div>

            <h5 style={{ marginBottom: 8, marginTop: 28 }}>Configuring in DepGuard</h5>
            <ol style={{ color: 'var(--bs-body-color)', lineHeight: 2 }}>
              <li>Navigate to <strong>Settings &amp; AI → Webhooks</strong> in the sidebar.</li>
              <li>Click <strong>Add Webhook</strong>.</li>
              <li>Choose the type (Slack / Teams / Generic) and paste the webhook URL.</li>
              <li>Select the events you want to subscribe to.</li>
              <li>Optionally set a <strong>minimum severity</strong> (e.g. CRITICAL/HIGH only) for <code>new_vulnerability</code>.</li>
              <li>Enter a <strong>secret</strong> to enable HMAC-SHA256 signature verification on the receiver side.</li>
              <li>Use the <strong>🧪 Test</strong> button to validate your configuration — the result is visible in the Delivery Log.</li>
            </ol>

            <h5 style={{ marginBottom: 8, marginTop: 28 }}>Security — Verifying the HMAC signature</h5>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Every payload includes an <code>X-DepGuard-Signature: sha256=&lt;hex&gt;</code> header.
              Verify this on the receiver side by computing the HMAC-SHA256 of the raw request body with your secret
              and comparing it to the header value. Node.js example:
            </p>
            <pre style={{ background: 'var(--bs-tertiary-bg)', borderRadius: 8, padding: 16, fontSize: '0.82rem', overflowX: 'auto', marginBottom: 16 }}>{`const crypto = require('crypto');

function verifyDepGuardSignature(rawBody, secret, signatureHeader) {
  const expected = 'sha256=' + crypto
    .createHmac('sha256', secret)
    .update(rawBody)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(expected),
    Buffer.from(signatureHeader)
  );
}`}</pre>

            <h5 style={{ marginBottom: 8, marginTop: 8 }}>Payload format (generic — example)</h5>
            <pre style={{ background: 'var(--bs-tertiary-bg)', borderRadius: 8, padding: 16, fontSize: '0.82rem', overflowX: 'auto' }}>{`{
  "event": "new_vulnerability",
  "timestamp": "2026-03-12T22:00:00Z",
  "data": {
    "repo": "my-service",
    "sbom_file": "abc123-sbom.json",
    "vuln_id": "CVE-2024-1234",
    "severity": "CRITICAL",
    "cvss": 9.8,
    "package": "lodash",
    "version": "4.17.20",
    "fixed_in": "4.17.21",
    "summary": "Prototype pollution vulnerability"
  }
}`}</pre>

            <Pills terms={['Slack', 'Microsoft Teams', 'Power Automate', 'n8n', 'Zapier', 'Webhook', 'HMAC-SHA256', 'Severity filter', 'Delivery log', 'Real-time alerts']} />
          </section>

          {/* ═══ Domain: 🤖 AI & Automation ═══ */}
          <div id="domain-ai" style={{
            borderTop: '2px solid var(--bs-border-color)',
            margin: '2rem 0 2rem',
            paddingTop: '1.5rem',
          }}>
            <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'var(--bs-secondary-color)',
              marginBottom: '0.25rem',
            }}>
              🤖 AI & Automation
            </div>
          </div>

          <section id="ai" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🤖" title={t('docs.sections.ai')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.ai.intro', 'DepGuard integrates with multiple AI providers to bring natural language interaction to your security data.')}
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Capabilities include:
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li>Natural language queries over your dependency database</li>
              <li>Repository health summaries for executive reporting</li>
              <li>Plain-language explanation of vulnerabilities and their exploitability</li>
              <li>Remediation strategy suggestions (upgrade paths, alternative packages)</li>
              <li>AI-generated fix suggestions for specific CVEs</li>
            </ul>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.ai.providers', 'Supported providers: GitHub Copilot CLI, GitHub Models, OpenAI, Anthropic Claude, and Ollama.')}
            </p>
            <WhyItMatters>
              {t('docs.content.ai.whyItMatters', 'Security and compliance data is only valuable if it is actionable.')}
            </WhyItMatters>
            <Pills terms={['GitHub Copilot CLI', 'GitHub Models', 'OpenAI', 'Anthropic', 'Ollama', 'GPT-4o', 'Natural language query', 'Remediation']} />
          </section>

          <section id="ai-agent" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🤖" title={t('docs.sections.ai-agent')} />

            <div style={{ background: '#fff3cd', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem', border: '1px solid #ffc107' }}>
              <strong>⚠️ Planned feature</strong> — {t('docs.content.ai-agent.planned', 'The Autonomous AI Security Agent is under active development and will be available in a future release.')}
            </div>

            {/* ── What is it ── */}
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.ai-agent.intro', 'The Autonomous AI Security Agent will be a proactive, background-running agent that continuously monitors all SBOMs.')}
            </p>

            <WhyItMatters>
              {t('docs.content.ai-agent.whyItMatters', 'Security teams today are overwhelmed. The Autonomous AI Security Agent eliminates the manual bottleneck.')}
            </WhyItMatters>

            {/* ── Current vs Agent ── */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '1rem' }}>
              Today vs with the Agent
            </h3>
            <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead>
                  <tr>
                    <th style={{ width: '40%' }}>🐌 Today (manual)</th>
                    <th>⚡ With the Autonomous Agent</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>OSV publishes new CVE → no action until next scan</td><td>Agent detects it immediately, scans all SBOMs</td></tr>
                  <tr><td>Engineer manually checks which repositories are affected</td><td>Agent identifies all affected packages across all repositories</td></tr>
                  <tr><td>Engineer decides exploitability (30–60 min per CVE)</td><td>Agent reasons over context, proposes or creates VEX statement</td></tr>
                  <tr><td>Remediation item created days later, often skipped</td><td>Agent creates remediation item with priority, owner, and due date</td></tr>
                  <tr><td>Slack alert is a raw CVE ID with no context</td><td>Agent sends summary: "3 repositories affected, 1 exploitable — action needed for repository X"</td></tr>
                  <tr><td>MTTR: 200+ days on average</td><td>Detection to action: minutes</td></tr>
                </tbody>
              </table>
            </div>

            {/* ── Technical Design ── */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Technical Design
            </h3>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The agent is implemented as a <strong>Go background goroutine</strong> orchestrating DepGuard's existing
              services. It does not require a new infrastructure component — it runs inside the existing API process
              and uses all the data and endpoints already present.
            </p>

            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '0.5rem' }}>Architecture</h4>
            <pre style={{
              background: 'var(--bs-tertiary-bg)', borderRadius: 'var(--radius-sm)',
              padding: '1rem 1.25rem', fontSize: '0.8rem', lineHeight: 1.7,
              border: '1px solid var(--bs-border-color)', overflowX: 'auto',
            }}>{`
┌─────────────────────────────────────────────────────────────────┐
│                    AI Security Agent (Go goroutine)              │
│                                                                   │
│  ┌──────────────┐    ┌──────────────┐    ┌───────────────────┐  │
│  │  Event Loop  │───▶│  Planner     │───▶│  Action Executor  │  │
│  │  (scheduler) │    │  (LLM-based) │    │  (calls services) │  │
│  └──────────────┘    └──────────────┘    └───────────────────┘  │
│         │                   │                      │             │
│    Triggers:           Reasoning:            Actions:            │
│    · New CVE           · Is it exploitable?  · Create VEX       │
│    · New SBOM          · Which repositories? · Create Remediation│
│    · Policy fail       · Priority?           · Send Webhook      │
│    · Daily digest      · Owner?              · Update Job status │
└─────────────────────────────────────────────────────────────────┘
         │                                           │
         ▼                                           ▼
┌─────────────────┐                    ┌─────────────────────────┐
│  Existing        │                    │  Existing Services       │
│  Data Sources    │                    │                          │
│  · OSV scanner   │                    │  · VEX endpoints         │
│  · SQLite SBOMs  │                    │  · Remediation endpoints │
│  · Policy engine │                    │  · Webhook dispatcher    │
│  · Audit log     │                    │  · Jobs table            │
└─────────────────┘                    └─────────────────────────┘
`}</pre>

            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '0.5rem' }}>Agent Loop</h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The agent runs on a configurable schedule (default: every 15 minutes) and is also triggered by events
              (new SBOM uploaded, new CVE detected). Each cycle:
            </p>
            <ol style={{ color: 'var(--bs-body-color)', lineHeight: 2, paddingLeft: '1.25rem' }}>
              <li><strong>Observe</strong> — query all SBOMs for new or unresolved vulnerabilities since the last agent run</li>
              <li><strong>Enrich</strong> — for each CVE, fetch full OSV details, CVSS score, fixedIn version, and affected package context</li>
              <li><strong>Reason</strong> — send a structured prompt to the configured AI provider (Copilot SDK / OpenAI / Anthropic) with SBOM context, asking: exploitability assessment, suggested VEX status, priority, and recommended action</li>
              <li><strong>Act</strong> — based on AI response: auto-create or update VEX statement; create remediation item (status: Acknowledged, with AI-generated notes); trigger webhook if severity threshold met</li>
              <li><strong>Log</strong> — write all agent actions to the audit log with actor <code>system:ai-agent</code></li>
            </ol>

            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '0.5rem' }}>AI Prompt Design</h4>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Each reasoning call sends a structured system prompt containing:
            </p>
            <pre style={{
              background: 'var(--bs-tertiary-bg)', borderRadius: 'var(--radius-sm)',
              padding: '1rem 1.25rem', fontSize: '0.8rem', lineHeight: 1.7,
              border: '1px solid var(--bs-border-color)', overflowX: 'auto',
            }}>{`System: You are a security triage expert for DepGuard, an SBOM security platform.
         Analyse the following CVE in the context of this repository and produce a
         structured JSON response.

Input:
{
  "cve_id":       "CVE-2024-XXXX",
  "cvss":         9.8,
  "package":      "pkg:npm/axios@0.21.1",
  "description":  "...",
  "repo":         "my-service",
  "sbom_context": { "direct_dependency": true, "usages": ["src/api/client.js:12"] },
  "fix_version":  "0.21.4"
}

Expected output:
{
  "vex_status":      "Exploitable" | "Not Exploitable" | "In Triage",
  "vex_justification": "...",
  "priority":        "Critical" | "High" | "Medium" | "Low",
  "recommended_action": "Upgrade to 0.21.4 in package.json",
  "suggested_owner": "team based on code ownership",
  "confidence":      0.0–1.0
}`}</pre>

            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '0.5rem' }}>Backend Implementation</h4>
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead><tr><th>Component</th><th>Location</th><th>Description</th></tr></thead>
                <tbody>
                  <tr><td><code>ai/agent.go</code></td><td>New file</td><td>Main agent loop, scheduler, event listener</td></tr>
                  <tr><td><code>ai/agent_planner.go</code></td><td>New file</td><td>LLM prompt construction and response parsing</td></tr>
                  <tr><td><code>ai/agent_actions.go</code></td><td>New file</td><td>Action executor: VEX, remediation, webhook, audit log</td></tr>
                  <tr><td><code>db/agent_state.go</code></td><td>New file</td><td>SQLite table: <code>agent_runs</code> (last_run, findings_processed, actions_taken)</td></tr>
                  <tr><td><code>Main.go</code></td><td>Extend</td><td>Start agent goroutine on startup if enabled in settings</td></tr>
                  <tr><td><code>GET /agent/status</code></td><td>New route</td><td>Returns last run time, findings processed, actions taken</td></tr>
                  <tr><td><code>POST /agent/run</code></td><td>New route</td><td>Trigger manual agent run (Admin only)</td></tr>
                  <tr><td><code>PUT /settings/agent</code></td><td>New route</td><td>Configure agent: enabled, schedule, auto-create VEX, severity threshold</td></tr>
                </tbody>
              </table>
            </div>

            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '0.5rem' }}>Frontend Implementation</h4>
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead><tr><th>Component</th><th>Description</th></tr></thead>
                <tbody>
                  <tr><td><code>AiAgentSettings.js</code></td><td>Settings card in the AI Settings page: enable/disable agent, schedule, severity threshold, auto-VEX toggle</td></tr>
                  <tr><td><code>AiAgentStatus.js</code></td><td>Dashboard widget showing last run, # findings processed, # actions taken, with "Run now" button</td></tr>
                  <tr><td>VulnerabilityView badge</td><td>CVEs triaged by the agent show a <code>🤖 AI triaged</code> badge with the agent's reasoning on hover</td></tr>
                  <tr><td>VEX statements</td><td>Agent-created VEX entries show <code>source: ai-agent</code> with confidence score</td></tr>
                  <tr><td>Remediation items</td><td>Agent-created items show <code>🤖</code> icon and AI-generated recommended action text</td></tr>
                </tbody>
              </table>
            </div>

            {/* ── Safeguards ── */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Safeguards & Human Oversight
            </h3>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The agent operates in two modes, configurable per-installation:
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 2, paddingLeft: '1.25rem' }}>
              <li><strong>Suggest mode</strong> (default) — the agent creates draft items flagged as <em>AI suggestion — pending review</em>.
                A human analyst must approve before they become active.</li>
              <li><strong>Auto mode</strong> (opt-in, Admin only) — the agent creates VEX and remediation items directly.
                All actions are logged to the audit log with actor <code>system:ai-agent</code> and can be rolled back.</li>
            </ul>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The AI provider call includes a <strong>confidence score</strong> in the response. Items with
              confidence &lt; 0.7 are always routed to suggest mode regardless of the configured setting.
              This ensures low-confidence decisions always get human review.
            </p>

            {/* ── Commercial Impact ── */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Commercial Impact
            </h3>
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
                <thead><tr><th>Dimension</th><th>Impact</th></tr></thead>
                <tbody>
                  <tr><td><strong>Cost savings</strong></td><td>A security team of 3 can handle the workload of 10 — directly quantifiable ROI for the sales pitch</td></tr>
                  <tr><td><strong>EU CRA compliance</strong></td><td>VEX statements are required under CRA — agent ensures they are always complete, no human gaps</td></tr>
                  <tr><td><strong>Competitive differentiation</strong></td><td>No competitor (Snyk, Dependency-Track, Mend) offers an autonomous agent scoped to SBOM+VEX+remediation</td></tr>
                  <tr><td><strong>Enterprise tier justification</strong></td><td>Agent is an Enterprise-tier feature — directly drives upsell from Professional to Enterprise</td></tr>
                  <tr><td><strong>MTTR reduction</strong></td><td>From 200+ days (industry average) to minutes for initial triage — measurable SLA improvement</td></tr>
                </tbody>
              </table>
            </div>

            <Pills terms={['Agentic AI', 'Triage', 'VEX', 'Remediation', 'SLSA', 'Autonomous', 'OSV', 'CVSS', 'Audit log', 'EU CRA', 'GitHub Copilot SDK', 'OpenAI', 'Anthropic']} />
          </section>

          {/* ═══ Domain: 🔎 Vigil SAST ═══ */}
          <div id="domain-vigil" style={{
            borderTop: '2px solid var(--bs-border-color)',
            margin: '2rem 0 2rem',
            paddingTop: '1.5rem',
          }}>
            <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'var(--bs-secondary-color)',
              marginBottom: '0.25rem',
            }}>
              🔎 Vigil SAST
            </div>
          </div>

          <section id="vigil-sast" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🔎" title="SAST Integration — Vigil in Repository View" />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              DepGuard embeds <strong>Vigil</strong> — an open-source SAST engine — directly into the Repository Details view.
              Teams can trigger static application security testing scans, track findings, and filter results without leaving
              the platform. Navigate to <strong>Repo Details → Code Analysis → SAST</strong> (the third sub-tab) to access the integration.
            </p>

            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Scan Summary Dashboard
            </h3>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The SAST tab opens with a summary card row for the <em>latest completed scan</em>:
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li><strong>Status</strong> — running / completed / failed, with live progress indicator during a scan</li>
              <li><strong>Total findings</strong> — count across all severities</li>
              <li><strong>Critical / High / Medium</strong> — broken-out severity counts with colour-coded badges</li>
              <li><strong>Rules run</strong> — number of Vigil rules applied in the scan</li>
              <li><strong>Duration</strong> — wall-clock time of the last scan</li>
            </ul>

            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Findings Table
            </h3>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              All findings are listed in a paginated table (25 per page) with the following columns:
            </p>
            <table className="table table-sm" style={{ fontSize: '0.8125rem', marginBottom: '1.25rem' }}>
              <thead><tr><th>Column</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td><strong>Severity</strong></td><td>Colour-coded badge — Critical (red), High (orange), Medium (yellow), Low (blue), Info (grey)</td></tr>
                <tr><td><strong>Rule ID</strong></td><td>Vigil rule identifier, e.g. <code>vigil:go/sql-injection</code></td></tr>
                <tr><td><strong>File path</strong></td><td>Relative path to the affected source file</td></tr>
                <tr><td><strong>Line</strong></td><td>Line number of the finding</td></tr>
                <tr><td><strong>Message</strong></td><td>Human-readable description of the security issue</td></tr>
              </tbody>
            </table>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Use the filter bar above the table to narrow findings by <strong>severity</strong>, <strong>rule ID</strong>,
              or <strong>file path</strong>. All filters are combinable.
            </p>

            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Running a Scan
            </h3>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Click the <strong>Run Scan</strong> button to trigger a new Vigil scan. The platform posts to the backend
              asynchronously and auto-polls until the scan completes — no manual refresh needed.
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Use the <strong>Scan History selector</strong> (last 10 scans) to switch between historical results and
              compare finding counts over time.
            </p>

            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              API Reference
            </h3>
            <pre style={{ background: 'var(--bs-tertiary-bg)', padding: '0.75rem', borderRadius: '6px', fontSize: '0.8rem' }}>
{`# Trigger a new Vigil scan (async)
POST /api/v1/vigil/scan
Body: { "repoPath": "/path/to/repo", "repoName": "my-repo" }
Response: { "id": "<scan-id>" }

# Poll / retrieve scan results
GET /api/v1/vigil/scan/{id}
Response: full scan object including status and findings array`}
            </pre>
            <WhyItMatters>
              SBOM-level vulnerability data tells you <em>what</em> is vulnerable. SAST tells you <em>where</em> in
              your own code the risk is introduced. Combining both in one platform means security teams can go from
              CVE disclosure to verified source location without switching tools.
            </WhyItMatters>
            <Pills terms={['SAST', 'Vigil', 'Static Analysis', 'Findings', 'Severity', 'Rule ID', 'Async scan', 'CI/CD', 'Semgrep', 'Repo Details']} />
          </section>

          <section id="vigil-studio" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🎨" title="Vigil Rule Studio" />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The <strong>Rule Studio</strong> is an integrated authoring environment for writing, generating, testing, and
              discovering Vigil security rules. Access it from the sidebar at <strong>Vigil → Rule Studio</strong>.
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The studio is organized into four tabs:
            </p>

            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Tab 1 — Generate (AI RuleSmith)
            </h3>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Describe a vulnerability class in plain language and the <strong>AI RuleSmith</strong> produces a complete,
              production-ready Vigil rule — including:
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li>Primary rule pattern + semantic variants for common bypass patterns</li>
              <li>Positive and negative test fixture cases</li>
              <li>Metadata block with CWE, severity, confidence, and references</li>
            </ul>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              The generated rule is immediately editable in the Write tab and can be pushed to the rule catalogue.
            </p>

            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Tab 2 — Write (Editor + Playground)
            </h3>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              A syntax-highlighted editor for crafting Vigil DSL rules, paired with a <strong>live playground</strong>{' '}
              that lets you paste a code snippet and see matches in real time — without installing anything locally.
              Supported features:
            </p>
            <ul style={{ color: 'var(--bs-body-color)', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              <li>Inline error highlighting for DSL syntax issues</li>
              <li>Split-pane view: rule on the left, code snippet and match results on the right</li>
              <li>One-click copy of the compiled Semgrep YAML output</li>
            </ul>

            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Tab 3 — Query (Joern CPGQL)
            </h3>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Execute <strong>Joern CPGQL</strong> queries directly against indexed repositories to explore the Code
              Property Graph (CPG). Use this to understand taint flows, find all callers of a specific sink, or build
              evidence for a new rule before formalising it in DSL.
            </p>

            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Tab 4 — Browse (Rule Discovery)
            </h3>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Search and filter the full rule catalogue. Filter by language, CWE, severity, or free-text. Clone any
              existing rule as a starting point in the Write tab.
            </p>
            <WhyItMatters>
              Writing high-precision SAST rules has historically required expert knowledge of engine internals. The Rule
              Studio lowers that bar dramatically: security engineers can describe a vulnerability in natural language,
              validate the rule interactively, and ship it to production without leaving the browser.
            </WhyItMatters>
            <Pills terms={['Rule Studio', 'RuleSmith', 'AI rule generation', 'Vigil DSL', 'Joern', 'CPGQL', 'CPG', 'Live playground', 'Semgrep YAML', 'CWE', 'Rule catalogue', 'SAST']} />
          </section>

          <section id="vigil-academy" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🎓" title="Vigil Academy" />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              <strong>Vigil Academy</strong> is an interactive learning platform embedded in DepGuard, accessible from{' '}
              <strong>Vigil → Academy</strong> in the sidebar. It teaches security engineers how to write effective SAST
              rules through seven progressive modules, with exercises and progress tracked per user session.
            </p>

            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              The 7 Learning Modules
            </h3>
            <table className="table table-sm" style={{ fontSize: '0.8125rem', marginBottom: '1.25rem' }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Module</th>
                  <th>What you learn</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td><strong>Rule Fundamentals</strong></td>
                  <td>DSL structure, metadata blocks, severity / confidence taxonomy, CWE identifiers</td>
                </tr>
                <tr>
                  <td>2</td>
                  <td><strong>Pattern Matching</strong></td>
                  <td>Syntactic patterns, wildcards, metavariables, multi-pattern rules</td>
                </tr>
                <tr>
                  <td>3</td>
                  <td><strong>Data Flow</strong></td>
                  <td>Taint sources, sinks, sanitisers; tracing user-controlled data through call chains</td>
                </tr>
                <tr>
                  <td>4</td>
                  <td><strong>Precision Engineering</strong></td>
                  <td>Reducing false positives with focus and constraints; false-negative / false-positive trade-offs</td>
                </tr>
                <tr>
                  <td>5</td>
                  <td><strong>Production Rules</strong></td>
                  <td>Test fixtures (<code>vigil test</code>), coverage measurement (<code>vigil coverage</code>), CI integration</td>
                </tr>
                <tr>
                  <td>6</td>
                  <td><strong>Joern Mastery</strong></td>
                  <td>CPG internals, CPGQL queries, call graph traversals, finding complex inter-procedural flows</td>
                </tr>
                <tr>
                  <td>7</td>
                  <td><strong>Security Engineering</strong></td>
                  <td>Translating CVEs, bug bounty reports, and threat models into rule specifications</td>
                </tr>
              </tbody>
            </table>

            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '2rem', marginBottom: '0.75rem' }}>
              Progress Tracking
            </h3>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Each module tracks exercise completion per user session. A progress bar on the Academy home screen shows
              how many modules and individual exercises have been completed. Progress is stored server-side so it
              persists across browser sessions.
            </p>
            <WhyItMatters>
              Enterprise security teams often struggle to adopt SAST tools because rule authoring has a steep learning
              curve. Vigil Academy closes that gap in-product, turning onboarding from a multi-week training exercise
              into a self-guided learning path that pays off within the first day.
            </WhyItMatters>
            <Pills terms={['Vigil Academy', 'SAST training', 'Rule authoring', 'Joern', 'Data flow', 'Taint analysis', 'CPG', 'CPGQL', 'Pattern matching', 'CWE', 'Progress tracking', 'Security engineering']} />
          </section>

          {/* ═══ Domain: ⚙️ Platform ═══ */}
          <div id="domain-platform" style={{
            borderTop: '2px solid var(--bs-border-color)',
            margin: '2rem 0 2rem',
            paddingTop: '1.5rem',
          }}>
            <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'var(--bs-secondary-color)',
              marginBottom: '0.25rem',
            }}>
              ⚙️ Platform
            </div>
          </div>

          <section id="auth" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="🔐" title={t('docs.sections.auth')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.content.auth.intro', 'DepGuard includes a built-in authentication system with JWT-based sessions and role-based access control (RBAC).')}
            </p>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              In addition to local email/password login, DepGuard now supports an <strong>Enterprise SSO</strong> flow based on
              <strong> OIDC</strong>. This first enterprise slice is designed for providers such as <strong>Microsoft Entra ID (Azure AD)</strong>
              and keeps the existing DepGuard JWT/RBAC model in place by exchanging the external identity for a normal DepGuard session.
            </p>

            <WhyItMatters>
              {t('docs.content.auth.whyItMatters', 'Multi-user authentication is a prerequisite for enterprise adoption.')}
            </WhyItMatters>

            <h5 style={{ marginTop: '1.5rem', marginBottom: '.75rem' }}>Roles</h5>
            <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Permissions</th>
                </tr>
              </thead>
              <tbody>
                <tr><td><code>admin</code></td><td>Full access: manage users, view audit log, change roles, delete users</td></tr>
                <tr><td><code>analyst</code></td><td>Read + write: upload SBOMs, create/edit VEX statements and remediation items</td></tr>
                <tr><td><code>viewer</code></td><td>Read-only: view all dashboards, reports and findings — no mutations allowed</td></tr>
              </tbody>
            </table>

            <h5 style={{ marginTop: '1.5rem', marginBottom: '.75rem' }}>First-run Setup</h5>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              When no users exist, the <strong>Register</strong> form is publicly accessible. The first account created
              automatically receives the <code>admin</code> role. After that, only existing admins can create new accounts
              via <strong>User Management → ➕ Invite</strong>.
            </p>

            <h5 style={{ marginTop: '1.5rem', marginBottom: '.75rem' }}>User Management (Admin)</h5>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              Navigate to <strong>⚙️</strong> in the sidebar footer (visible to admins only) to open the User Management page.
              It has three tabs:
            </p>
            <ul style={{ lineHeight: 2 }}>
              <li><strong>👤 Users</strong> — List all users, change roles inline, delete accounts</li>
              <li><strong>➕ Invite</strong> — Create a new user account with email, password and role</li>
              <li><strong>📋 Audit Log</strong> — Last 100 security events with timestamp, actor, action, resource and IP</li>
              <li><strong>🏢 SSO</strong> — Configure OIDC provider name, issuer URL, client ID/secret, redirect URL, scopes, allowed domains, default role, and auto-provisioning</li>
            </ul>

            <h5 style={{ marginTop: '1.5rem', marginBottom: '.75rem' }}>Enterprise SSO (OIDC / Azure AD)</h5>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              When OIDC is enabled in <strong>User Management → SSO</strong>, the login page shows an enterprise sign-in button.
              Users are redirected to the configured identity provider, authenticated there, and then returned to DepGuard with a
              freshly issued application JWT. This means all existing role checks, audit logging, and API authorization continue to work
              without introducing a second session model.
            </p>
            <ul style={{ lineHeight: 2 }}>
              <li><strong>Supported now</strong> — OIDC provider discovery, authorization-code start/callback flow, domain restrictions, default role mapping, and optional automatic user provisioning</li>
              <li><strong>Good fit for</strong> — Microsoft Entra ID / Azure AD and other OIDC-compatible enterprise identity providers</li>
              <li><strong>Still planned</strong> — broader SAML / LDAP coverage and deeper enterprise identity hardening</li>
            </ul>

            <h5 style={{ marginTop: '1.5rem', marginBottom: '.75rem' }}>How to determine the Issuer URL</h5>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              On the <strong>User Management → SSO → Enterprise Sign-In</strong> page, the <code>Issuer URL</code> must be the base OIDC issuer,
              not the full metadata-document URL. DepGuard uses that issuer to automatically fetch the provider discovery document.
            </p>
            <ul style={{ lineHeight: 2 }}>
              <li><strong>Microsoft Entra ID / Azure AD</strong> — In Azure Portal, open <strong>App registrations</strong> → your app → <strong>Endpoints</strong>, copy the <strong>OpenID Connect metadata document</strong> URL, then remove <code>/.well-known/openid-configuration</code> from the end.</li>
              <li><strong>Typical Entra issuer format</strong> — <code>https://login.microsoftonline.com/&lt;tenant-id&gt;/v2.0</code></li>
              <li><strong>Tenant ID value</strong> — You can use the Azure tenant GUID or your tenant domain, depending on how your identity team publishes the app.</li>
              <li><strong>Generic OIDC providers</strong> — Open the provider’s metadata document and copy the value of the <code>issuer</code> field, or use the metadata URL minus <code>/.well-known/openid-configuration</code>.</li>
              <li><strong>Common mistake</strong> — Do not paste the full metadata-document URL into the field; use only the issuer base URL.</li>
            </ul>

            <h5 style={{ marginTop: '1.5rem', marginBottom: '.75rem' }}>API Authentication</h5>
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              All API calls require the header <code>Authorization: Bearer &lt;token&gt;</code>.
              Obtain a token via <code>POST /auth/login</code> for local auth, or via the OIDC browser flow for enterprise sign-in.
              Tokens expire after <strong>24 hours</strong>.
            </p>
            <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
              <thead>
                <tr><th>Endpoint</th><th>Method</th><th>Auth required</th><th>Description</th></tr>
              </thead>
              <tbody>
                <tr><td><code>/auth/register</code></td><td>POST</td><td>None (first-run) / Admin</td><td>Create account. First account → admin.</td></tr>
                <tr><td><code>/auth/providers</code></td><td>GET</td><td>None</td><td>Returns available sign-in providers for the login page</td></tr>
                <tr><td><code>/auth/login</code></td><td>POST</td><td>None</td><td>Returns JWT token + user object</td></tr>
                <tr><td><code>/auth/oidc/start</code></td><td>GET</td><td>None</td><td>Starts enterprise OIDC sign-in and redirects to the identity provider</td></tr>
                <tr><td><code>/auth/oidc/callback</code></td><td>GET</td><td>None</td><td>Completes enterprise sign-in, provisions/updates the user, and redirects back with an app token</td></tr>
                <tr><td><code>/auth/me</code></td><td>GET</td><td>Any role</td><td>Returns current user profile</td></tr>
                <tr><td><code>/auth/logout</code></td><td>POST</td><td>Any role</td><td>Writes audit entry (token invalidation is client-side)</td></tr>
                <tr><td><code>/auth/sso/settings</code></td><td>GET</td><td>Admin</td><td>Returns the current enterprise SSO configuration</td></tr>
                <tr><td><code>/auth/sso/settings</code></td><td>PUT</td><td>Admin</td><td>Saves the enterprise SSO configuration</td></tr>
                <tr><td><code>/admin/users</code></td><td>GET</td><td>Admin</td><td>List all users</td></tr>
                <tr><td><code>/admin/users/:id/role</code></td><td>PUT</td><td>Admin</td><td>Change user role</td></tr>
                <tr><td><code>/admin/users/:id</code></td><td>DELETE</td><td>Admin</td><td>Delete a user</td></tr>
                <tr><td><code>/admin/audit-log</code></td><td>GET</td><td>Admin</td><td>Retrieve audit log (default: last 100)</td></tr>
              </tbody>
            </table>

            <Pills terms={['JWT', 'RBAC', 'Bearer token', 'OIDC', 'Enterprise SSO', 'Azure AD', 'Audit log', 'Admin', 'Analyst', 'Viewer', 'SOC 2', 'Separation of duties']} />
          </section>

          <section id="glossary" style={{ marginBottom: '3rem', scrollMarginTop: '1.5rem' }}>
            <SectionHeading icon="📖" title={t('docs.sections.glossary')} />
            <p style={{ color: 'var(--bs-body-color)', lineHeight: 1.7 }}>
              {t('docs.glossaryIntro')}
            </p>
            <table className="table table-sm" style={{ fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  <th style={{ width: '140px' }}>{t('docs.term')}</th>
                  <th>{t('docs.definition')}</th>
                </tr>
              </thead>
              <tbody>
                {GLOSSARY_KEYS.map(([term, key]) => (
                  <tr key={`${term}-${key}`}>
                    <td><code style={{ fontSize: '0.8125rem' }}>{term}</code></td>
                    <td>{t(`docs.glossary.${key}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

        </div>
      </div>
    </div>
  );
}
